/*
  Gaden — a pixel-art Nyhavn where every window is a cover a customer bought.
  Shared by sections/street.liquid and preview/index.html; both put the same
  JSON in a <script type="application/json" data-street-data> inside a
  [data-street] element, and this file does the rest.

  Data shape:
  {
    "palette":  { "Baby Blue": "#A5DAE8", ... },          // filament name → hex
    "settings": { "windowsPerBuilding": 12, "shopfronts": true,
                  "shopSlots": 2, "contactUrl": "/pages/contact",
                  "shopUrl": "/collections/all", "pageUrl": "/pages/gaden",
                  "goal": 50, "goalText": "…", "goalReachedText": "…",
                  "inviteBelow": 15, "families": { "Pastel": ["Baby Blue"] },
                  "covers": { "cover-1-modul-1pin.svg": "<asset url>" },
                  "minBuildings": 6, "deepLink": true },
    "windows":  [{ "id": "vindue-1023-1", "model": "2x1 modul, vandret",
                   "line": "Base", "cells": "1,2", "frame": "Bone White",
                   "front": "Baby Blue", "switch": "Terracotta",
                   "month": "2026-10",
                   "featured": false, "url": "/products/..." }],
    "shopfronts": [{ "brand": "", "logo": "", "url": "", "active": true }]
  }

  Colours may be palette names or hex codes. `model` may be the bare size
  ("2½ modul") or a full product title ("Til LK Fuga Soft — 2½ modul").
  `cells` is one entry per switch field: 1, 2 (kontakter) or stik, either
  as "1,2,stik" or as the order's Kontakter value ("1 + 2 (øverst → nederst)").

  The street is pixel art in the spirit of Pokémon Gold: chunky pixels, ink
  outlines and three tones per material. Each building is a small <canvas>
  drawn at 1 pixel per art pixel and scaled up by CSS with
  `image-rendering: pixelated`. The scale is a whole number of device
  pixels, so every art pixel stays square. Colours come from the --street-*
  custom properties in street.css. Buildings are only drawn while near the
  viewport.

  Copenhagen around it: the street starts at Kongens Nytorv (the Nyhavn
  memorial anchor, a pølsevogn), Rundetårn and Vor Frelsers Kirke stand in
  the haze behind the roofs, the canal is an animated water tile, and a
  tour boat, a sailing boat, a GoBoat or a kayak passes now and then.

  The covers in the windows are not pixel art: they are the product page's
  own drawings (assets/cover-*.svg), composed per field and coloured exactly
  as the customer ordered them, so a window shows the real thing. They sit
  on top of the canvas as images, so the lamp flicker can repaint the room
  behind them freely.
*/
(function () {
  'use strict';

  // ---- Geometry (art pixels) ------------------------------------------------
  // Chunky, Game Boy Color-sized pixels: a window is 12 × 16 art pixels and
  // the whole scene is 176 tall, so one art pixel is 3–4 screen pixels.
  var WIN_W = 12;
  var WIN_H = 16;
  var FLOOR = 22;
  var GROUND = 34;
  var ROOFZ = 26;
  var SKY = 4;
  var QUAY = 7;
  var CANAL = 17;
  var MAX_FLOORS = 4;
  var H = SKY + ROOFZ + MAX_FLOORS * FLOOR + GROUND + QUAY + CANAL;
  var GROUND_Y = H - QUAY - CANAL; // top of the quay
  var GF_TOP = GROUND_Y - GROUND; // top of the ground floor
  var LOT_W = 60;
  var PLAZA_W = 76;
  var TILE = 16; // width of one water tile

  var FACADES = 8;
  var ROOMS = 4;
  var DOORS = 4;
  var HULLS = 3;

  var MONTHS = ['januar', 'februar', 'marts', 'april', 'maj', 'juni', 'juli', 'august', 'september', 'oktober', 'november', 'december'];

  // Read once from street.css; shades are derived.
  var COLOR_NAMES = [
    'ink', 'paper', 'cloud', 'trim', 'frame-alt', 'stone', 'beam',
    'roof', 'roof-tile', 'chimney', 'dark-glass', 'glint', 'curtain',
    'room-1', 'room-2', 'room-3', 'room-4', 'room-lit', 'lampshade', 'floor',
    'door-1', 'door-2', 'door-3', 'door-4',
    'shop-sign', 'shop-sign-text', 'shop-glass', 'awning',
    'cobble', 'quay-edge', 'water', 'water-light', 'bollard',
    'hull-1', 'hull-2', 'hull-3', 'flag', 'parasol-1', 'parasol-2',
    'flowerbox', 'flower-1', 'flower-2', 'flower-3', 'leaf', 'fence', 'crane', 'far',
    'facade-1', 'facade-2', 'facade-3', 'facade-4', 'facade-5', 'facade-6', 'facade-7', 'facade-8',
  ];

  // ---- Cover models -----------------------------------------------------------
  // dir: how the fields sit (h = side by side, v = stacked).
  // m = a field the customer set (1 or 2 kontakter, or stik),
  // s = a fixed small socket, S = a large (1½ modul) socket.
  var MODELS = {
    '1 modul': { dir: 'h', cells: ['m'] },
    '2x1 modul, vandret': { dir: 'h', cells: ['m', 'm'] },
    '2x1 modul, lodret': { dir: 'v', cells: ['m', 'm'] },
    '2x1 modul': { dir: 'v', cells: ['m', 'm'] },
    '3x1 modul, vandret': { dir: 'h', cells: ['m', 'm', 'm'] },
    '3x1 modul, lodret': { dir: 'v', cells: ['m', 'm', 'm'] },
    '3x1 modul': { dir: 'v', cells: ['m', 'm', 'm'] },
    '1½ modul': { dir: 'v', cells: ['S'] },
    '2x1½ modul': { dir: 'h', cells: ['S', 'S'] },
    '3x1½ modul': { dir: 'h', cells: ['S', 'S', 'S'] },
    '2x2 modul, vandret': { dir: 'h', cells: ['s', 's', 's', 's'] },
    '2½ modul': { dir: 'v', cells: ['m', 'S'] },
    '3½ modul': { dir: 'v', cells: ['m', 'm', 'S'] },
  };

  // ---- A 3×5 bitmap font for the shop signs --------------------------------
  var FONT = {
    A: '010101111101101', B: '110101110101110', C: '011100100100011', D: '110101101101110',
    E: '111100110100111', F: '111100110100100', G: '011100101101011', H: '101101111101101',
    I: '111010010010111', J: '001001001101010', K: '101101110101101', L: '100100100100111',
    M: ['10001', '11011', '10101', '10001', '10001'], N: ['1001', '1101', '1011', '1001', '1001'],
    O: '010101101101010', P: '110101110100100', Q: '010101101110011', R: '110101110101101',
    S: '011100010001110', T: '111010010010010', U: '101101101101111', V: '101101101101010',
    W: ['10001', '10001', '10101', '11011', '10001'], X: '101101010101101', Y: '101101010010010',
    Z: '111001010100111', 'Æ': ['0111', '1010', '1111', '1010', '1011'], 'Ø': ['0111', '1011', '1101', '1001', '1110'],
    'Å': '010000111101101',
    0: '111101101101111', 1: '010110010010111', 2: '110001010100111', 3: '110001010001110',
    4: '101101111001001', 5: '111100110001110', 6: '011100111101111', 7: '111001010010010',
    8: '111101111101111', 9: '111101111001110',
    '?': '110001010000010', '!': ['1', '1', '1', '0', '1'], '.': ['0', '0', '0', '0', '1'],
    ',': ['00', '00', '00', '01', '10'], '-': '000000111000000', "'": ['1', '1', '0', '0', '0'],
    '&': '010101010101011', '/': '001001010100100', ' ': ['00', '00', '00', '00', '00'],
  };
  for (var g in FONT) {
    if (typeof FONT[g] === 'string') FONT[g] = FONT[g].match(/.{3}/g);
  }

  function glyph(ch) {
    if (FONT[ch]) return FONT[ch];
    var base = ch.normalize ? ch.normalize('NFD').charAt(0) : ch;
    return FONT[base] || FONT[' '];
  }

  function textWidth(s) {
    var w = 0;
    for (var i = 0; i < s.length; i++) w += glyph(s.charAt(i))[0].length + (i ? 1 : 0);
    return w;
  }

  function wrapText(str, maxW, maxLines) {
    var words = String(str || '').toUpperCase().trim().split(/\s+/);
    var lines = [];
    var cur = '';
    words.forEach(function (w) {
      var next = cur ? cur + ' ' + w : w;
      if (textWidth(next) <= maxW || !cur) cur = next;
      else {
        lines.push(cur);
        cur = w;
      }
    });
    if (cur) lines.push(cur);
    lines = lines.slice(0, maxLines);
    return lines.map(function (l) {
      while (l.length > 1 && textWidth(l) > maxW) l = l.slice(0, -1);
      return l;
    });
  }

  // ---- Small helpers --------------------------------------------------------------
  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function pct(n, of) {
    return Math.round((n / of) * 100000) / 1000 + '%';
  }

  // mulberry32 — a seeded random, so the street looks the same on every visit
  // and a building never changes when windows are added after it.
  function rng(seed) {
    var a = seed >>> 0;
    return function () {
      a = (a + 0x6d2b79f5) >>> 0;
      var t = a;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function pick(rand, n) {
    return Math.floor(rand() * n);
  }

  function shuffle(rand, list) {
    for (var i = list.length - 1; i > 0; i--) {
      var j = pick(rand, i + 1);
      var t = list[i];
      list[i] = list[j];
      list[j] = t;
    }
    return list;
  }

  function darken(hex, f) {
    var m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex).trim());
    if (!m) return hex;
    var h = m[1].length === 3 ? m[1].replace(/./g, '$&$&') : m[1];
    var out = '#';
    for (var i = 0; i < 3; i++) {
      var v = Math.round(parseInt(h.substr(i * 2, 2), 16) * (1 - f));
      out += ('0' + v.toString(16)).slice(-2);
    }
    return out;
  }

  function lighten(hex, f) {
    var m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex).trim());
    if (!m) return hex;
    var h = m[1].length === 3 ? m[1].replace(/./g, '$&$&') : m[1];
    var out = '#';
    for (var i = 0; i < 3; i++) {
      var v = parseInt(h.substr(i * 2, 2), 16);
      v = Math.round(v + (255 - v) * f);
      out += ('0' + v.toString(16)).slice(-2);
    }
    return out;
  }

  function readColors(root) {
    var cs = getComputedStyle(root);
    var C = {};
    COLOR_NAMES.forEach(function (n) {
      C[n] = cs.getPropertyValue('--street-' + n).trim() || '#ff00ff';
    });
    return C;
  }

  // ---- Data → windows --------------------------------------------------------------
  function parseModel(raw) {
    var title = String(raw || '').trim();
    var size = title.indexOf(' — ') > -1 ? title.split(' — ').pop().trim() : title;
    var key = size.replace(/1\.5/g, '1½').replace(/2\.5/g, '2½').replace(/3\.5/g, '3½');
    return {
      name: MODELS[key] ? key : size || '1 modul',
      spec: MODELS[key] || MODELS['1 modul'],
      line: /soft/i.test(title) ? 'Soft' : /base/i.test(title) ? 'Base' : '',
    };
  }

  // "1,2,stik" or the order's Kontakter value: "2 kontakter",
  // "1 + 2 (øverst → nederst)", "1 stik + 2 kontakter (…)".
  function parseCells(spec, raw) {
    var given = String(raw || '')
      .replace(/\s*\(.*\)\s*$/, '')
      .split(/[,;|+]/)
      .filter(function (s) {
        return s.trim();
      })
      .map(function (s) {
        s = s.trim().toLowerCase();
        if (s.indexOf('stik') === 0) return 'stik';
        return parseInt(s, 10) === 2 ? 2 : 1;
      });
    var k = 0;
    return spec.cells.map(function (c) {
      if (c !== 'm') return c;
      var v = given[k++];
      return v === undefined ? 1 : v;
    });
  }

  function describe(cells) {
    var kontakter = 0;
    var stik = 0;
    cells.forEach(function (c) {
      if (c === 1 || c === 2) kontakter += c;
      else stik += 1;
    });
    var parts = [];
    if (kontakter) parts.push(kontakter + (kontakter === 1 ? ' kontakt' : ' kontakter'));
    if (stik) parts.push(stik + (stik === 1 ? ' stikkontakt' : ' stikkontakter'));
    return { text: parts.join(' + '), hasSwitch: kontakter > 0 };
  }

  function resolveColor(palette, v) {
    if (!v) return null;
    v = String(v).trim();
    if (v.charAt(0) === '#') {
      var hex = v.toUpperCase();
      for (var name in palette) {
        if (String(palette[name]).toUpperCase() === hex) return { name: name, hex: palette[name] };
      }
      return { name: hex, hex: hex };
    }
    for (var n in palette) {
      if (n.toLowerCase() === v.toLowerCase()) return { name: n, hex: palette[n] };
    }
    return { name: v, hex: '#CFC8BA' };
  }

  function monthLabel(raw) {
    var m = /^(\d{4})-(\d{1,2})/.exec(String(raw || ''));
    if (!m) return '';
    return MONTHS[parseInt(m[2], 10) - 1] + ' ' + m[1];
  }

  function normalizeWindow(w, palette) {
    var model = parseModel(w.model);
    var cells = parseCells(model.spec, w.cells);
    var d = describe(cells);
    var frame = resolveColor(palette, w.frame) || { name: '—', hex: '#EFEDE7' };
    var front = resolveColor(palette, w.front) || frame;
    var sw = d.hasSwitch ? resolveColor(palette, w['switch']) || front : null;
    var month = monthLabel(w.month);
    var line = w.line || model.line;
    var label = 'Vindue: ' + d.text + ', ramme ' + frame.name + ', forside ' + front.name;
    if (sw) label += ', kontakt ' + sw.name;
    if (month) label += ', ' + month;
    return {
      id: String(w.id || ''),
      model: model.name,
      line: line,
      title: (line ? 'LK Fuga ' + line + ' · ' : '') + model.name,
      spec: model.spec,
      cells: cells,
      desc: d.text,
      frame: frame,
      front: front,
      sw: sw,
      month: month,
      sortKey: String(w.month || ''),
      featured: !!w.featured,
      url: w.url || '',
      label: label,
    };
  }

  // ---- The real cover drawings --------------------------------------------------
  // The same size → file mapping as product-color-customizer.liquid and
  // cart-cover-thumb.liquid. Sizes with switch fields compose per field from
  // the 1-kontakt, 2-kontakter and socket drawings; the rest are one file.
  var COVER_FILES = {
    '1 modul': ['cover-1-modul-1pin.svg', 'cover-1-modul-2pins.svg', 'cover-1-modul-stik.svg'],
    '2½ modul': ['cover-2-5-modul-1pin.svg', 'cover-2-5-modul-2pins.svg'],
    '3½ modul': ['cover-3-5-modul-1pin.svg', 'cover-3-5-modul-2pins.svg'],
    '2x1 modul': ['cover-2x1-modul-lodret-1pin.svg', 'cover-2x1-modul-lodret-2pins.svg', 'cover-2x1-modul-lodret-stik.svg'],
    '2x1 modul, lodret': ['cover-2x1-modul-lodret-1pin.svg', 'cover-2x1-modul-lodret-2pins.svg', 'cover-2x1-modul-lodret-stik.svg'],
    '2x1 modul, vandret': ['cover-2x1-modul-vandret-1pin.svg', 'cover-2x1-modul-vandret-2pins.svg', 'cover-2x1-modul-vandret-stik.svg'],
    '3x1 modul': ['cover-3x1-modul-lodret-1pin.svg', 'cover-3x1-modul-lodret-2pins.svg', 'cover-3x1-modul-lodret-stik.svg'],
    '3x1 modul, lodret': ['cover-3x1-modul-lodret-1pin.svg', 'cover-3x1-modul-lodret-2pins.svg', 'cover-3x1-modul-lodret-stik.svg'],
    '3x1 modul, vandret': ['cover-3x1-modul-vandret-1pin.svg', 'cover-3x1-modul-vandret-2pins.svg', 'cover-3x1-modul-vandret-stik.svg'],
    '1½ modul': ['cover-1-5-modul.svg'],
    '2x1½ modul': ['cover-2x1-5-modul.svg'],
    '3x1½ modul': ['cover-3x1-5-modul.svg'],
    '2x2 modul': ['cover-2x2-modul-vandret.svg'],
    '2x2 modul, vandret': ['cover-2x2-modul-vandret.svg'],
  };

  var svgDocs = {};
  var coverUrls = {};

  function svgDoc(file, opts) {
    if (!svgDocs[file]) {
      var inline = opts.coverSvgs && opts.coverSvgs[file];
      var text = inline
        ? Promise.resolve(inline)
        : fetch((opts.covers && opts.covers[file]) || (opts.coverBase || '') + file).then(function (r) {
            if (!r.ok) throw new Error(file);
            return r.text();
          });
      svgDocs[file] = text.then(function (t) {
        var doc = new DOMParser().parseFromString(t, 'image/svg+xml');
        if (doc.querySelector('parsererror') || doc.documentElement.tagName.toLowerCase() !== 'svg') throw new Error(file);
        return doc;
      });
      svgDocs[file].catch(function () {
        delete svgDocs[file];
      });
    }
    return svgDocs[file];
  }

  // A blob: URL of the window's cover, composed and coloured. Colours are
  // written into the markup (an <img> can't see CSS variables), so the same
  // image works in the street, the card and the share picture.
  function coverUrl(win, opts) {
    var states = win.cells.filter(function (c) {
      return c === 1 || c === 2 || c === 'stik';
    });
    var sw = (win.sw || win.front).hex;
    var key = [win.model, states.join('.'), win.frame.hex, win.front.hex, sw].join('|');
    if (coverUrls[key]) return coverUrls[key];
    var files = COVER_FILES[win.model] || COVER_FILES['1 modul'];
    var need2 = states.indexOf(2) > -1 && files[1];
    var needStik = states.indexOf('stik') > -1 && files[2];
    coverUrls[key] = Promise.all([
      svgDoc(files[0], opts),
      need2 ? svgDoc(files[1], opts) : null,
      needStik ? svgDoc(files[2], opts) : null,
    ]).then(function (docs) {
      var one = docs[0];
      var from = { 2: docs[1], stik: docs[2] };
      var svg = one.documentElement.cloneNode(true);
      if (files.length > 1) {
        // As on the product page: start from the 1-kontakt drawing and swap
        // in each field's group(s) from the 2-kontakter or socket drawing.
        [].slice.call(svg.querySelectorAll('.cover-pin')).forEach(function (g) {
          var cell = parseInt(g.getAttribute('data-cell'), 10);
          var source = from[states[cell]];
          if (!source) return;
          [].forEach.call(source.querySelectorAll('.cover-pin[data-cell="' + cell + '"]'), function (p) {
            g.parentNode.insertBefore(p.cloneNode(true), g);
          });
          g.parentNode.removeChild(g);
        });
      }
      var title = svg.querySelector('title');
      if (title) title.parentNode.removeChild(title);
      var markup = new XMLSerializer()
        .serializeToString(svg)
        .replace(/var\(--cover-frame[^)]*\)/g, win.frame.hex)
        .replace(/var\(--cover-front[^)]*\)/g, win.front.hex)
        .replace(/var\(--cover-switch[^)]*\)/g, sw)
        .replace(/var\(--[\w-]+\s*,\s*([^)]+)\)/g, '$1');
      return URL.createObjectURL(new Blob([markup], { type: 'image/svg+xml' }));
    });
    coverUrls[key].catch(function () {
      delete coverUrls[key];
    });
    return coverUrls[key];
  }

  function loadImage(src) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.onload = function () {
        resolve(img);
      };
      img.onerror = reject;
      img.src = src;
    });
  }

  // ---- Drawing primitives -----------------------------------------------------
  // Colours are names from street.css. '~name' is a darker shade and '^name'
  // a lighter one, so every material gets the three tones of a GBC tile.
  function Pen(ctx, C) {
    this.ctx = ctx;
    this.C = C;
    this.shades = {};
  }

  Pen.prototype.col = function (c) {
    if (c.charAt(0) === '#') return c;
    var k = c.charAt(0);
    if (k === '~' || k === '^') {
      if (!this.shades[c]) this.shades[c] = k === '~' ? darken(this.col(c.slice(1)), 0.16) : lighten(this.col(c.slice(1)), 0.4);
      return this.shades[c];
    }
    return this.C[c] || c;
  };

  Pen.prototype.rect = function (x, y, w, h, c) {
    if (w <= 0 || h <= 0) return;
    this.ctx.fillStyle = this.col(c);
    this.ctx.fillRect(x, y, w, h);
  };

  Pen.prototype.px = function (x, y, c) {
    this.rect(x, y, 1, 1, c);
  };

  // A filled box with a one-pixel ink outline.
  Pen.prototype.box = function (x, y, w, h, c) {
    this.rect(x, y, w, h, 'ink');
    this.rect(x + 1, y + 1, w - 2, h - 2, c);
  };

  // A small bitmap: one string per row, each character looked up in `map`
  // ('.' is see-through).
  Pen.prototype.sprite = function (rows, x, y, map) {
    for (var r = 0; r < rows.length; r++) {
      for (var k = 0; k < rows[r].length; k++) {
        var ch = rows[r].charAt(k);
        if (ch !== '.') this.px(x + k, y + r, map[ch] || 'ink');
      }
    }
  };

  Pen.prototype.text = function (s, x, y, c) {
    for (var i = 0; i < s.length; i++) {
      var gl = glyph(s.charAt(i));
      for (var r = 0; r < gl.length; r++) {
        for (var k = 0; k < gl[r].length; k++) if (gl[r].charAt(k) === '1') this.px(x + k, y + r, c);
      }
      x += gl[0].length + 1;
    }
  };

  // A gable or roof outline, row by row from the top: fn(t) gives the half
  // width (0–1) at depth t, and the ink edge follows every step.
  Pen.prototype.gable = function (x0, base, w, gh, fn, fill) {
    var mid = x0 + w / 2;
    var pl = null;
    var pr = null;
    for (var k = 0; k < gh; k++) {
      var hw = Math.max(1, Math.round(fn((k + 1) / gh) * (w / 2)));
      var L = Math.round(mid - hw);
      var R = Math.round(mid + hw) - 1;
      var y = base - gh + k;
      this.rect(L, y, R - L + 1, 1, fill);
      if (pl === null) this.rect(L, y, R - L + 1, 1, 'ink');
      else {
        this.rect(L, y, Math.max(1, pl - L), 1, 'ink');
        var rs = Math.min(R, pr + 1);
        this.rect(rs, y, R - rs + 1, 1, 'ink');
      }
      pl = L;
      pr = R;
    }
  };

  // A straight one-pixel line; calls fn(x, y, i) for each point if given.
  Pen.prototype.line = function (x0, y0, x1, y1, c, fn) {
    var n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
    for (var i = 0; i <= n; i++) {
      var x = Math.round(x0 + ((x1 - x0) * i) / (n || 1));
      var y = Math.round(y0 + ((y1 - y0) * i) / (n || 1));
      this.px(x, y, c);
      if (fn) fn(x, y, i);
    }
  };

  var PROFILES = {
    pointed: function (t) {
      return 0.08 + 0.92 * t;
    },
    stepped: function (t) {
      return 0.2 + 0.8 * Math.min(1, (Math.floor(t * 4) + 1) / 4);
    },
    curved: function (t) {
      if (t < 0.28) return 0.2;
      var u = (t - 0.28) / 0.72;
      return 0.2 + 0.8 * u * u * (3 - 2 * u);
    },
    bell: function (t) {
      return 0.1 + 0.9 * Math.sin((t * Math.PI) / 2);
    },
  };

  // ---- Planning the street ------------------------------------------------------
  function gridChoices(n) {
    var all = [];
    for (var c = 2; c <= 4; c++) for (var f = 3; f <= MAX_FLOORS; f++) all.push({ cols: c, floors: f });
    var near = all.filter(function (g) {
      return Math.abs(g.cols * g.floors - n) <= Math.max(3, Math.round(n * 0.34));
    });
    return near.length ? near : [all.reduce(function (a, b) {
      return Math.abs(a.cols * a.floors - n) <= Math.abs(b.cols * b.floors - n) ? a : b;
    })];
  }

  function colX(bl, c) {
    return bl.side + c * (WIN_W + bl.gap);
  }

  function planBuilding(b, grid, needsShop, prev) {
    var rand = rng(9001 + b * 7919);
    var cols = needsShop ? Math.max(3, grid.cols) : grid.cols;
    var floors = grid.floors;
    var side = 3 + pick(rand, 3);
    var gap = 3 + pick(rand, 2);
    var W = 2 * side + cols * WIN_W + (cols - 1) * gap;

    var facade = 1 + pick(rand, FACADES - 1);
    if (facade >= prev.facade) facade += 1;
    var tops = ['eaves', 'stepped', 'pointed', 'curved', 'bell', 'mansard', 'eaves'];
    var top = tops[pick(rand, tops.length)];
    if (top === prev.top) top = tops[(tops.indexOf(top) + 1 + pick(rand, tops.length - 1)) % tops.length];

    var bl = {
      index: b,
      cols: cols,
      floors: floors,
      side: side,
      gap: gap,
      W: W,
      top: top,
      facade: 'facade-' + facade,
      frame: rand() < 0.78 ? 'trim' : 'frame-alt',
      hood: ['lintel', 'arch', 'key', 'none'][pick(rand, 4)],
      texture: ['dots', 'dots', 'courses', 'plain'][pick(rand, 4)],
      bands: rand() < 0.5,
      groundShade: rand() < 0.4,
      tile: rand() < 0.55,
      rh: 7 + pick(rand, 3),
      kvist: rand() < 0.75,
      gh: 17 + pick(rand, 6),
      dormers: [],
      chimneys: [],
      door: 1 + pick(rand, DOORS),
      doorCol: rand() < 0.5 ? 0 : cols - 1,
      ground: needsShop ? 'shop' : rand() < 0.4 ? 'cafe' : 'cellar',
      parasol: 1 + pick(rand, 2),
      boat: rand() < 0.6,
      hull: 1 + pick(rand, HULLS),
      boatW: 22 + pick(rand, 8),
      mastFloors: pick(rand, 3),
      signalFlags: rand() < 0.6,
      bollards: rand() < 0.7,
      quoins: rand() < 0.35,
      balcony: rand() < 0.35 ? pick(rand, cols) : -1,
      lantern: rand() < 0.6,
      bikes: pick(rand, 3),
      bikeColor: 'door-' + (1 + pick(rand, DOORS)),
      cargo: rand() < 0.35,
      hollyhocks: rand() < 0.5,
      lifering: rand() < 0.3,
      gull: rand() < 0.3,
      seed: 4242 + b * 131,
      slots: [],
    };
    bl.top0 = GF_TOP - floors * FLOOR;
    for (var d = 0; d < cols; d++) bl.dormers.push(rand() < 0.6);
    bl.chimneys.push(4 + pick(rand, 6));
    if (rand() < 0.5) bl.chimneys.push(W - 9 - pick(rand, 4));

    // Slots fill in a shuffled order that only depends on the building, so
    // a new sale lands in a fresh window and the old ones stay put.
    var order = [];
    for (var r = 0; r < floors; r++) for (var c = 0; c < cols; c++) order.push({ r: r, c: c });
    shuffle(rand, order).forEach(function (o) {
      bl.slots.push({
        r: o.r,
        c: o.c,
        x: colX(bl, o.c),
        y: bl.top0 + o.r * FLOOR + 5,
        room: 1 + pick(rand, ROOMS),
        empty: rand() < 0.6 ? 'curtain' : 'dark',
        lamp: rand() < 0.35,
      });
    });
    return bl;
  }

  function planStreet(windows, shops, opts) {
    var n = Math.max(6, Math.min(16, parseInt(opts.windowsPerBuilding, 10) || 12));
    var choices = gridChoices(n);
    var shopAt = {};
    shops.forEach(function (s, k) {
      shopAt[1 + k * 2] = s;
    });

    var buildings = [];
    var placed = 0;
    var prev = { facade: 0, top: '' };
    var minCount = Math.max(opts.minBuildings || 6, shops.length * 2 + 1);
    for (var b = 0; placed <= windows.length || b < minCount; b++) {
      var gr = rng(77 + b * 104729);
      var pool = choices.filter(function (c) {
        return c.floors !== prev.floors;
      });
      var grid = pool.length && gr() < 0.65 ? pool[pick(gr, pool.length)] : choices[pick(gr, choices.length)];
      var bl = planBuilding(b, grid, !!shopAt[b], prev);
      bl.shop = shopAt[b] || null;
      bl.slots.forEach(function (slot) {
        slot.index = placed;
        slot.win = windows[placed] || null;
        placed++;
      });
      prev = { facade: parseInt(bl.facade.slice(7), 10), top: bl.top, floors: bl.floors };
      buildings.push(bl);
    }
    return buildings;
  }

  // ---- Drawing a building ------------------------------------------------------------
  // Each floor: row 0 is the sill shadow of the floor above, 3–4 the hood,
  // 5–20 the window, 21 the sill.
  function drawHood(p, bl, x, y) {
    if (bl.hood === 'lintel') {
      p.rect(x - 1, y - 2, WIN_W + 2, 1, 'ink');
      p.rect(x - 1, y - 1, WIN_W + 2, 1, 'trim');
    } else if (bl.hood === 'arch') {
      p.rect(x + 2, y - 2, 8, 1, 'ink');
      p.rect(x, y - 1, 2, 1, 'ink');
      p.rect(x + 2, y - 1, 8, 1, 'trim');
      p.rect(x + 10, y - 1, 2, 1, 'ink');
    } else if (bl.hood === 'key') {
      p.box(x + 4, y - 3, 4, 4, 'trim');
    }
  }

  // A window: a 3-row transom over an 8-row pane. A sold window shows a
  // room (the cover is laid over it as an image); an empty one shows glass
  // with a glint or drawn curtains.
  function drawWindow(p, bl, slot, lit) {
    var x = slot.x;
    var y = slot.y;
    var ix = x + 2;
    p.box(x, y, WIN_W, WIN_H, bl.frame);
    if (slot.win) {
      var room = lit ? 'room-lit' : 'room-' + slot.room;
      p.rect(ix, y + 2, 8, 3, room);
      p.rect(ix, y + 6, 8, 8, room);
      p.px(ix, y + 2, '^' + room);
      p.rect(ix, y + 12, 8, 1, '~' + room);
      p.rect(ix, y + 13, 8, 1, 'floor');
    } else if (slot.empty === 'curtain') {
      p.rect(ix, y + 2, 8, 3, 'dark-glass');
      p.px(ix + 1, y + 3, 'glint');
      p.rect(ix, y + 6, 8, 8, 'curtain');
      p.rect(ix + 3, y + 6, 2, 8, '~curtain');
      p.rect(ix + 1, y + 7, 1, 7, '~curtain');
      p.rect(ix + 6, y + 7, 1, 7, '~curtain');
      p.rect(ix, y + 6, 8, 1, '^curtain');
    } else {
      p.rect(ix, y + 2, 8, 3, 'dark-glass');
      p.rect(ix, y + 6, 8, 8, 'dark-glass');
      p.rect(ix + 3, y + 6, 2, 8, bl.frame);
      p.px(ix + 1, y + 3, 'glint');
      p.px(ix + 1, y + 9, 'glint');
      p.px(ix + 2, y + 8, 'glint');
      p.px(ix + 5, y + 12, 'glint');
      p.px(ix + 6, y + 11, 'glint');
    }
  }

  function drawSill(p, bl, slot) {
    var x = slot.x;
    var y = slot.y + WIN_H;
    p.rect(x - 1, y, WIN_W + 2, 1, 'trim');
    p.rect(x - 1, y + 1, WIN_W + 2, 1, '~' + bl.facade);
    if (slot.win && slot.win.featured) {
      [0, 2, 5, 7, 10].forEach(function (fx, k) {
        p.px(x + fx, y - 4, 'flower-' + ((k % 3) + 1));
        p.px(x + fx + 1, y - 4, 'leaf');
      });
      p.box(x - 1, y - 3, WIN_W + 2, 3, 'flowerbox');
    }
  }

  function smallWindow(p, x, y, w, h, frame) {
    p.box(x, y, w, h, frame);
    p.rect(x + 1, y + 1, w - 2, h - 3, 'dark-glass');
    p.px(x + 1, y + 1, 'glint');
  }

  function drawHoist(p, mid, y) {
    p.rect(mid - 1, y, 3, 1, 'ink');
    p.rect(mid - 1, y + 1, 3, 1, 'beam');
    p.rect(mid, y + 2, 1, 2, 'ink');
  }

  function drawChimney(p, x, y, h) {
    p.box(x, y, 4, h, 'chimney');
    p.rect(x + 1, y + 1, 1, h - 2, '^chimney');
    p.rect(x - 1, y - 1, 6, 1, 'ink');
  }

  function drawTop(p, bl) {
    var T = bl.top0;
    var W = bl.W;
    var mid = Math.floor(W / 2);
    var roof = bl.tile ? 'roof-tile' : 'roof';

    if (bl.top === 'eaves' || bl.top === 'mansard') {
      var rh = bl.top === 'mansard' ? 10 : bl.rh;
      var fill = bl.top === 'mansard' ? 'roof' : roof;
      bl.chimneys.forEach(function (x) {
        drawChimney(p, x, T - rh - 4, rh + 4);
      });
      var slope = bl.top === 'mansard' ? 3 : 1;
      p.gable(0, T, W, rh, function (t) {
        return 1 - ((1 - t) * rh) / slope / (W / 2);
      }, fill);
      // Striped courses like a Pokémon roof: a lit row, a shadow row.
      for (var k = 1; k < rh; k++) {
        var inset = Math.ceil((rh - k) / slope) + 1;
        var y = T - rh + k;
        if (k % 2 === 0) p.rect(inset, y, W - 2 * inset, 1, '~' + fill);
        else for (var x = inset + ((k >> 1) % 2) * 2; x < W - inset; x += 4) p.px(x, y, '^' + fill);
      }
      if (bl.top === 'mansard') {
        for (var c = 0; c < bl.cols; c++) {
          if (!bl.dormers[c]) continue;
          var dx = colX(bl, c) + 3;
          p.rect(dx + 1, T - rh, 4, 1, 'ink');
          smallWindow(p, dx, T - rh + 1, 6, 7, bl.frame);
        }
      } else if (bl.kvist) {
        // The front dormer (kvist) with its hoist beam.
        var kw = Math.min(W - 8, 12 + (bl.cols > 2 ? 4 : 0));
        var kh = rh + 3;
        var kx = Math.round(mid - kw / 2);
        p.box(kx, T - kh, kw, kh + 1, bl.facade);
        var ph = Math.floor(kw / 2) - 1;
        p.gable(kx, T - kh + 1, kw, ph, PROFILES.pointed, bl.facade);
        smallWindow(p, mid - 3, T - kh + 2, 6, 7, bl.frame);
        drawHoist(p, mid, T - kh - ph + 3);
      }
    } else {
      var gh = bl.top === 'pointed' || bl.top === 'stepped' ? Math.min(26, Math.round(W * 0.5)) : bl.gh;
      if (bl.chimneys.length > 1) drawChimney(p, W - 9, T - gh + 4, gh - 2);
      p.gable(0, T, W, gh, PROFILES[bl.top], bl.facade);
      p.gable(2, T, W - 4, gh - 3, PROFILES[bl.top], bl.facade);
      if (bl.top === 'curved' || bl.top === 'bell') {
        p.box(mid - 1, T - gh - 2, 3, 3, 'trim');
        p.px(mid, T - gh - 3, 'ink');
      }
      if (W >= 40) {
        smallWindow(p, mid - 8, T - 9, 5, 7, bl.frame);
        smallWindow(p, mid + 3, T - 9, 5, 7, bl.frame);
      } else smallWindow(p, mid - 3, T - 9, 6, 7, bl.frame);
      var upper = T - gh + Math.round(gh * 0.4);
      if (bl.top === 'pointed' || bl.top === 'bell') {
        // A round attic window.
        p.rect(mid - 1, upper - 2, 2, 1, 'ink');
        p.rect(mid - 2, upper - 1, 4, 2, 'ink');
        p.rect(mid - 1, upper - 1, 2, 2, 'dark-glass');
        p.px(mid - 1, upper - 1, 'glint');
        p.rect(mid - 1, upper + 1, 2, 1, 'ink');
      } else drawHoist(p, mid, upper - 1);
    }
    // Cornice.
    p.rect(0, T, W, 1, 'ink');
    p.rect(0, T + 1, W, 1, 'trim');
    p.rect(1, T + 2, W - 2, 1, '~' + bl.facade);
  }

  // Plaster with a sparse dither, or rusticated courses.
  function drawTexture(p, bl, y0, y1) {
    var sh = '~' + bl.facade;
    if (bl.texture === 'dots') {
      for (var y = y0 + 1, k = 0; y < y1; y += 3, k++) {
        for (var x = 3 + (k % 2) * 3; x < bl.W - 2; x += 6) p.px(x, y, sh);
      }
    } else if (bl.texture === 'courses') {
      for (var yy = y0 + 3; yy < y1; yy += 4) {
        for (var xx = 2; xx < bl.W - 2; xx += 2) p.px(xx, yy, sh);
      }
    }
  }

  function shopGeom(bl) {
    var lines = wrapText(bl.shop.placeholder ? 'Din butik her?' : bl.shop.brand || '', bl.W - 8, 2);
    var sx = 2;
    var sw = bl.W - 4;
    var sy = GF_TOP + 4;
    var sh = lines.length * 6 + 2;
    var ay = sy + sh;
    var gy = ay + 5;
    var gx1 = bl.doorCol === 0 ? colX(bl, 1) - 1 : 3;
    var gx2 = bl.doorCol === 0 ? bl.W - 3 : colX(bl, bl.cols - 1);
    return { lines: lines, sx: sx, sw: sw, sy: sy, sh: sh, ay: ay, gx: gx1, gy: gy, gw: gx2 - gx1, gh: GROUND_Y - 3 - gy };
  }

  function drawShop(p, bl) {
    var s = shopGeom(bl);
    p.box(s.sx, s.sy, s.sw, s.sh, 'shop-sign');
    s.lines.forEach(function (l, i) {
      p.text(l, Math.round(s.sx + (s.sw - textWidth(l)) / 2), s.sy + 2 + i * 6, 'shop-sign-text');
    });
    // Striped awning with a scalloped hem.
    for (var x = s.sx - 1; x < s.sx + s.sw + 1; x++) {
      var on = Math.floor((x - s.sx + 1) / 2) % 2 === 0;
      p.rect(x, s.ay, 1, 3, on ? 'awning' : 'trim');
      if (on) p.px(x, s.ay + 3, 'awning');
    }
    p.rect(s.sx - 1, s.ay, s.sw + 2, 1, 'ink');
    p.box(s.gx, s.gy, s.gw, s.gh, 'shop-glass');
    for (var m = s.gx + 12; m < s.gx + s.gw - 5; m += 12) p.rect(m, s.gy + 1, 1, s.gh - 2, 'ink');
    p.px(s.gx + 2, s.gy + s.gh - 3, '^shop-glass');
    p.px(s.gx + 3, s.gy + s.gh - 4, '^shop-glass');
    return s;
  }

  function drawDoor(p, bl, top) {
    var dx = colX(bl, bl.doorCol) + 2;
    var dw = 8;
    var dy = top;
    var dh = GROUND_Y - 1 - dy;
    var door = 'door-' + bl.door;
    p.box(dx - 1, dy - 1, dw + 2, dh + 2, 'trim');
    p.rect(dx, dy, dw, dh, door);
    p.rect(dx + 1, dy, dw - 2, 2, 'dark-glass');
    p.px(dx + 1, dy, 'glint');
    p.rect(dx + 1, dy + 4, 2, 5, '~' + door);
    p.rect(dx + 5, dy + 4, 2, 5, '~' + door);
    p.rect(dx + 1, dy + 11, 2, Math.max(2, dh - 14), '~' + door);
    p.rect(dx + 5, dy + 11, 2, Math.max(2, dh - 14), '~' + door);
    p.rect(dx, dy + 2, 1, dh - 2, '^' + door);
    p.px(dx + 4, dy + Math.round(dh * 0.55), 'lampshade');
  }

  function drawGround(p, bl) {
    var g = GF_TOP;
    var W = bl.W;
    if (bl.groundShade) p.rect(1, g, W - 2, GROUND, '~' + bl.facade);
    p.rect(0, g, W, 1, 'ink');
    p.rect(0, g + 1, W, 1, 'trim');
    p.rect(0, g + 2, W, 1, 'ink');
    p.rect(1, GROUND_Y - 3, W - 2, 1, 'ink');
    p.rect(1, GROUND_Y - 2, W - 2, 2, 'stone');
    for (var j = 4; j < W - 1; j += 6) p.px(j, GROUND_Y - 2, '~stone');

    var doorTop = g + 8;
    if (bl.shop) doorTop = drawShop(p, bl).gy + 1;
    else {
      for (var c = 0; c < bl.cols; c++) {
        if (c === bl.doorCol) continue;
        var x = colX(bl, c);
        if (bl.ground === 'cafe') {
          var col = 'parasol-' + bl.parasol;
          p.rect(x - 1, g + 5, WIN_W + 2, 1, 'ink');
          for (var ax = x - 1; ax < x + WIN_W + 1; ax++) {
            var on = Math.floor((ax - x + 1) / 2) % 2 === 0;
            p.rect(ax, g + 6, 1, 3, on ? col : 'trim');
            if (on) p.px(ax, g + 9, col);
          }
          var wh = GROUND_Y - 4 - (g + 11);
          p.box(x - 1, g + 11, WIN_W + 2, wh, bl.frame);
          p.rect(x, g + 12, WIN_W, wh - 2, 'room-lit');
          p.rect(x, g + 12, WIN_W, 1, '^room-lit');
          p.rect(x + 5, g + 12, 2, wh - 2, bl.frame);
          p.rect(x + 1, GROUND_Y - 9, 3, 1, 'floor');
          p.rect(x + 8, GROUND_Y - 9, 3, 1, 'floor');
        } else {
          smallWindow(p, x + 1, g + 6, WIN_W - 2, 13, bl.frame);
          p.rect(x + 5, g + 7, 2, 11, bl.frame);
          p.px(x + 3, g + 14, 'glint');
          p.rect(x, g + 19, WIN_W, 1, 'trim');
          p.rect(x, g + 20, WIN_W, 1, '~' + bl.facade);
          // Half-basement window just above the pavement.
          p.box(x + 2, GROUND_Y - 9, 8, 5, 'trim');
          p.rect(x + 3, GROUND_Y - 8, 6, 3, 'dark-glass');
          p.px(x + 3, GROUND_Y - 8, 'glint');
        }
      }
    }
    drawDoor(p, bl, doorTop);
    drawDoorDetails(p, bl, doorTop);
  }

  function mastPositions(bl) {
    var xs = [Math.floor(bl.side / 2), bl.W - 1 - Math.floor(bl.side / 2)];
    for (var c = 0; c < bl.cols - 1; c++) xs.push(colX(bl, c) + WIN_W + Math.floor(bl.gap / 2));
    return xs;
  }

  // Cobbles in a running bond, a kerb, and an ink edge. The canal below is
  // left see-through: the animated water tiles (.street__canal) show there.
  function drawQuay(p, W) {
    var Q = GROUND_Y;
    p.rect(0, Q, W, 5, 'cobble');
    for (var r = 0; r < 5; r++) {
      var off = (Math.floor(r / 2) % 2) * 2;
      for (var x = 0; x < W; x++) {
        var m = (x + off) % 4;
        if (m === 0) p.px(x, Q + r, '~cobble');
        else if (r % 2 === 0 && m === 1) p.px(x, Q + r, '^cobble');
      }
    }
    p.rect(0, Q + 5, W, 1, 'quay-edge');
    p.rect(0, Q + 6, W, 1, 'ink');
  }

  // Small Dannebrog, w × 5, with the cross towards the hoist.
  function dannebrog(p, x, y, w, hoistLeft) {
    p.rect(x, y, w, 5, 'flag');
    p.rect(x, y + 2, w, 1, 'paper');
    p.rect(hoistLeft ? x + 2 : x + w - 3, y, 1, 5, 'paper');
  }

  var SIGNAL = ['flag', 'paper', 'flower-2', 'hull-2'];

  // An old wooden ship moored at the quay, with Dannebrog at the masthead
  // and a line of signal flags down to the bow and stern.
  function drawBoat(p, bl, rand) {
    var Q = GROUND_Y;
    var masts = mastPositions(bl);
    var mx = masts[pick(rand, masts.length)];
    var bw = Math.min(bl.boatW, bl.W - 4);
    var bx = Math.max(2, Math.min(bl.W - bw - 2, mx - Math.round(bw * 0.45)));
    if (mx < bx + 3 || mx > bx + bw - 4) mx = null;
    var hull = 'hull-' + bl.hull;
    var top = Q + 4;
    var rows = 6;
    var mastTop = GF_TOP - bl.mastFloors * FLOOR + 2;
    if (mx !== null && bl.signalFlags && bl.mastFloors === 0) {
      [bx + 1, bx + bw - 2].forEach(function (ex) {
        p.line(mx, mastTop + 6, ex, top - 1, 'ink', function (x, y, i) {
          if (i % 3 === 1 && i > 1) {
            var c = SIGNAL[(i / 3 + ex) % SIGNAL.length | 0];
            p.rect(x, y + 1, 2, 2, c);
          }
        });
      });
    }
    for (var k = 0; k < rows; k++) {
      var l = bx + Math.floor(k / 2);
      var r = bx + bw - 1 - Math.floor(k * 0.8);
      p.rect(l, top + k, r - l + 1, 1, k === 1 ? 'trim' : k === 2 ? '^' + hull : hull);
      p.px(l, top + k, 'ink');
      p.px(r, top + k, 'ink');
    }
    p.rect(bx, top, bw, 1, 'ink');
    var lb = bx + Math.floor((rows - 1) / 2);
    p.rect(lb, top + rows - 1, bx + bw - Math.floor((rows - 1) * 0.8) - lb, 1, 'ink');
    p.rect(bx + 2, top + rows, bw - 6, 1, 'water-light');
    // Cabin and a rail.
    p.box(bx + 3, top - 5, 8, 6, 'trim');
    p.rect(bx + 2, top - 6, 10, 1, 'ink');
    p.rect(bx + 4, top - 3, 6, 1, 'dark-glass');
    p.px(bx + 4, top - 3, 'glint');
    for (var rx = bx + 13; rx < bx + bw - 3; rx += 2) p.px(rx, top - 1, 'ink');
    p.rect(bx + 13, top - 2, Math.max(0, bw - 16), 1, 'ink');
    if (mx !== null) {
      p.rect(mx, mastTop, 1, top - mastTop, 'beam');
      p.rect(mx - 2, mastTop + 6, 5, 1, 'beam');
      p.rect(mx - 1, mastTop + 7, 3, 1, 'paper');
      dannebrog(p, mx + 1, mastTop, 6, true);
    }
  }

  var BIKE = ['.##...#..', '..ccccc..', '.#..#..#.', '#.#...#.#', '.#.....#.'];
  // A Christiania cargo bike: a wooden box in front.
  var CARGO = ['......######', '.##...#bbbb#', '..cccc#bbbb#', '.#..#.######', '#.#......#.#', '.#........#.'];
  var GULL_SIT = ['..kk.', '.kwwy', 'kwww.', '.k.k.'];
  var LIFERING = ['.rwr.', 'w...w', 'r...r', 'w...w', '.rwr.'];
  var WHEEL = ['.#.', '#.#', '.#.'];

  // Corner quoins and a balcony.
  function drawFacadeDetails(p, bl) {
    var T = bl.top0;
    if (bl.quoins) {
      for (var y = T + 4, k = 0; y < GF_TOP - 2; y += 3, k++) {
        var w = k % 2 ? 2 : 3;
        p.rect(1, y, w, 2, '~' + bl.facade);
        p.rect(bl.W - 1 - w, y, w, 2, '~' + bl.facade);
      }
    }
    if (bl.balcony > -1) {
      var x = colX(bl, bl.balcony);
      var y0 = bl.top0 + (bl.floors - 1) * FLOOR + 5 + 12;
      p.rect(x - 2, y0, WIN_W + 4, 1, 'ink');
      for (var bx = x - 2; bx < x + WIN_W + 2; bx += 2) p.rect(bx, y0 + 1, 1, 3, 'ink');
      p.rect(x - 3, y0 + 4, WIN_W + 6, 1, 'trim');
      p.rect(x - 3, y0 + 5, WIN_W + 6, 1, 'ink');
    }
  }

  // A lantern and a house number beside the door.
  function drawDoorDetails(p, bl, doorTop) {
    var dx = colX(bl, bl.doorCol) + 2;
    var left = bl.doorCol > 0 || bl.side >= 4;
    var lx = left ? dx - 5 : dx + 10;
    if (bl.lantern) {
      p.rect(lx, doorTop + 1, 3, 1, 'ink');
      p.box(lx, doorTop + 2, 3, 4, 'lampshade');
      p.px(lx + 1, doorTop + 6, 'ink');
    }
    var nx = left ? dx + 10 : dx - 5;
    if (nx > 1 && nx + 4 < bl.W) {
      p.box(nx, doorTop + 8, 4, 3, 'paper');
      p.px(nx + 1, doorTop + 9, 'ink');
    }
  }

  // Stokroser: the tall hollyhocks that grow out of the pavement.
  function drawHollyhock(p, x, h, k) {
    var Q = GROUND_Y;
    p.rect(x, Q - h, 1, h, 'leaf');
    for (var y = Q - h + 1, i = 0; y < Q - 3; y += 2, i++) p.px(x + (i % 2 ? 1 : -1), y, i % 3 === k % 3 ? 'flower-1' : 'flower-3');
    p.px(x, Q - h - 1, 'flower-3');
    p.px(x + 1, Q - 2, 'leaf');
    p.px(x - 1, Q - 4, 'leaf');
  }

  function drawProps(p, bl, rand) {
    var Q = GROUND_Y;
    var dx = colX(bl, bl.doorCol) + 2;
    p.rect(dx - 2, Q, 12, 1, 'stone');
    p.rect(dx - 2, Q + 1, 12, 1, '~stone');
    if (bl.hollyhocks && !(bl.ground === 'cafe' && !bl.shop)) {
      if (dx - 3 > 1) drawHollyhock(p, dx - 3, 15, 0);
      if (dx + 10 < bl.W - 2) drawHollyhock(p, dx + 10, 12, 1);
    }
    for (var i = 0; i < bl.bikes; i++) {
      var bx = 3 + pick(rand, Math.max(1, bl.W - 16));
      if (bx + 12 > dx - 4 && bx < dx + 12) continue;
      if (bl.cargo && i === 0) p.sprite(CARGO, bx, Q - 4, { c: bl.bikeColor, b: 'beam' });
      else p.sprite(BIKE, bx, Q - 3, { c: bl.bikeColor });
    }
    if (bl.ground === 'cafe' && !bl.shop) {
      for (var c = 0; c < bl.cols; c++) {
        if (c === bl.doorCol) continue;
        var px = colX(bl, c) + 6;
        var col = 'parasol-' + bl.parasol;
        p.rect(px - 4, Q - 7, 9, 1, 'ink');
        p.rect(px - 5, Q - 6, 11, 1, '^' + col);
        p.rect(px - 6, Q - 5, 13, 1, col);
        p.rect(px - 6, Q - 4, 13, 1, 'ink');
        p.rect(px, Q - 3, 1, 5, 'ink');
        p.rect(px - 3, Q + 2, 7, 1, 'ink');
        p.px(px - 2, Q + 3, 'ink');
        p.px(px + 2, Q + 3, 'ink');
      }
    }
    if (bl.bollards) {
      var ox = 4 + pick(rand, Math.max(1, bl.W - 10));
      p.rect(ox - 1, Q, 5, 1, 'ink');
      p.rect(ox, Q + 1, 3, 4, 'bollard');
      p.rect(ox, Q + 1, 1, 3, '^bollard');
      if (bl.gull) p.sprite(GULL_SIT, ox - 1, Q - 4, { k: 'ink', w: 'cloud', y: 'lampshade' });
    }
    if (bl.lifering) {
      // At the end away from the door, so it never hangs in front of it.
      var lx = bl.doorCol === 0 ? bl.W - 9 : 3;
      p.rect(lx + 2, Q - 7, 1, 12, 'beam');
      p.sprite(LIFERING, lx, Q - 5, { r: 'flag', w: 'paper' });
    }
  }

  function drawBuilding(ctx, C, bl) {
    var p = new Pen(ctx, C);
    var rand = rng(bl.seed);
    var T = bl.top0;
    var W = bl.W;
    drawTop(p, bl);
    p.rect(0, T + 3, W, GF_TOP - T - 3, bl.facade);
    drawTexture(p, bl, T + 3, GF_TOP);
    if (bl.bands) for (var r = 1; r < bl.floors; r++) p.rect(1, T + r * FLOOR + 1, W - 2, 1, '~' + bl.facade);
    p.rect(0, GF_TOP, W, GROUND, bl.facade);
    drawTexture(p, bl, GF_TOP + 3, GROUND_Y - 3);
    // Lit left edge, shaded right edge.
    p.rect(1, T + 3, 1, GROUND_Y - T - 6, '^' + bl.facade);
    p.rect(W - 2, T + 3, 1, GROUND_Y - T - 6, '~' + bl.facade);
    bl.slots.forEach(function (slot) {
      drawHood(p, bl, slot.x, slot.y);
      drawWindow(p, bl, slot, false);
      drawSill(p, bl, slot);
    });
    drawFacadeDetails(p, bl);
    drawGround(p, bl);
    p.rect(0, T, 1, GROUND_Y - T, 'ink');
    p.rect(W - 1, T, 1, GROUND_Y - T, 'ink');
    drawQuay(p, W);
    drawProps(p, bl, rand);
    if (bl.boat) drawBoat(p, bl, rand);
  }

  // The empty lot at the end: a crane and a hoarding.
  function drawLot(ctx, C) {
    var p = new Pen(ctx, C);
    var W = LOT_W;
    var Q = GROUND_Y;
    var fy = Q - 16;
    var mast = 50;
    var jibY = SKY + 14;
    p.rect(mast - 2, jibY, 1, fy - jibY, 'ink');
    p.rect(mast + 2, jibY, 1, fy - jibY, 'ink');
    for (var y = fy - 1; y > jibY + 3; y -= 4) {
      p.rect(mast - 1, y, 3, 1, 'crane');
      p.px(mast - 1, y - 1, 'crane');
      p.px(mast, y - 2, 'crane');
      p.px(mast + 1, y - 3, 'crane');
    }
    p.rect(2, jibY, W - 3, 1, 'ink');
    p.rect(2, jibY + 3, mast - 4, 1, 'ink');
    for (var x = 3; x < mast - 2; x += 3) p.rect(x, jibY + 1, 1, 2, 'crane');
    p.rect(mast - 1, jibY - 5, 3, 5, 'crane');
    p.box(W - 8, jibY - 2, 6, 4, 'crane');
    p.rect(12, jibY + 4, 1, 34, 'ink');
    p.rect(11, jibY + 38, 3, 1, 'ink');
    p.px(13, jibY + 39, 'ink');
    p.box(0, fy, W, 16, 'fence');
    for (var bx = 4; bx < W - 1; bx += 4) p.rect(bx, fy + 1, 1, 14, '~fence');
    p.box(2, fy + 1, 47, 14, 'trim');
    p.text('PLADS TIL', 26 - Math.round(textWidth('PLADS TIL') / 2), fy + 3, 'ink');
    p.text('FLERE HJEM', 26 - Math.round(textWidth('FLERE HJEM') / 2), fy + 9, 'ink');
    drawQuay(p, W);
  }

  // The start of the street, at Kongens Nytorv: the memorial anchor, a
  // pølsevogn, a street lamp and a flagpole.
  function drawPlaza(ctx, C) {
    var p = new Pen(ctx, C);
    var Q = GROUND_Y;
    drawQuay(p, PLAZA_W);
    // Mindeankeret on its granite plinth.
    p.box(4, Q - 6, 21, 7, 'stone');
    p.rect(5, Q - 5, 19, 1, '^stone');
    p.rect(14, Q - 28, 2, 21, 'ink');
    p.rect(13, Q - 32, 4, 1, 'ink');
    p.rect(12, Q - 31, 1, 2, 'ink');
    p.rect(17, Q - 31, 1, 2, 'ink');
    p.rect(13, Q - 29, 4, 1, 'ink');
    p.rect(7, Q - 25, 16, 2, 'ink');
    p.rect(12, Q - 8, 6, 1, 'ink');
    [[11, 9], [10, 10], [9, 11], [8, 12], [8, 13]].forEach(function (a) {
      p.rect(a[0] - 1, Q - a[1], 2, 1, 'ink');
      p.rect(29 - a[0], Q - a[1], 2, 1, 'ink');
    });
    p.rect(5, Q - 15, 3, 2, 'ink');
    p.px(6, Q - 16, 'ink');
    p.rect(22, Q - 15, 3, 2, 'ink');
    p.px(23, Q - 16, 'ink');
    p.sprite(GULL_SIT, 19, Q - 10, { k: 'ink', w: 'cloud', y: 'lampshade' });
    // Pølsevogn.
    p.box(29, Q - 20, 30, 7, 'flag');
    p.text('PØLSER', 44 - Math.round(textWidth('PØLSER') / 2), Q - 19, 'paper');
    p.box(30, Q - 14, 28, 12, 'paper');
    p.rect(33, Q - 12, 16, 4, 'room-lit');
    p.rect(35, Q - 10, 3, 1, 'flag');
    p.rect(41, Q - 10, 3, 1, 'flag');
    p.rect(32, Q - 8, 18, 1, 'beam');
    p.rect(31, Q - 6, 26, 2, 'flag');
    p.rect(52, Q - 12, 4, 6, 'dark-glass');
    p.rect(58, Q - 4, 3, 1, 'ink');
    p.sprite(WHEEL, 34, Q - 2, {});
    p.sprite(WHEEL, 51, Q - 2, {});
    // Street lamp.
    p.rect(66, Q - 30, 1, 30, 'ink');
    p.rect(65, Q - 2, 3, 2, 'ink');
    p.rect(66, Q - 30, 4, 1, 'ink');
    p.rect(68, Q - 29, 3, 1, 'ink');
    p.rect(68, Q - 28, 3, 2, 'lampshade');
    p.rect(68, Q - 26, 3, 1, 'ink');
    // Flagpole.
    p.rect(73, Q - 44, 1, 44, 'ink');
    p.px(73, Q - 45, 'lampshade');
    dannebrog(p, 66, Q - 44, 7, false);
  }

  // Animated canal: two frames of a 16 × 17 tile, stacked.
  var WAVES = [[[2, 4], [10, 8], [5, 12], [13, 15]], [[4, 4], [12, 8], [7, 12], [15, 15]]];

  function waterTile(C) {
    var cv = document.createElement('canvas');
    cv.width = TILE;
    cv.height = CANAL * 2;
    var p = new Pen(cv.getContext('2d'), C);
    for (var f = 0; f < 2; f++) {
      var y0 = f * CANAL;
      p.rect(0, y0, TILE, CANAL, 'water');
      p.rect(0, y0, TILE, 1, '~water');
      for (var x = f; x < TILE; x += 2) p.px(x, y0 + 1, '~water');
      WAVES[f].forEach(function (w) {
        for (var k = 0; k < 3; k++) p.px((w[0] + k) % TILE, y0 + w[1], 'water-light');
        p.px((w[0] + TILE - 1) % TILE, y0 + w[1] + 1, 'water-light');
        p.px((w[0] + 3) % TILE, y0 + w[1] + 1, 'water-light');
      });
    }
    return cv;
  }

  // Boats that sail through now and then, bow to the right.
  var SAILERS = [
    { name: 'rundfart', speed: 6, w: 40, h: 12, draw: function (p) {
      dannebrog(p, 0, 0, 5, false);
      p.rect(5, 0, 1, 7, 'ink');
      p.box(6, 3, 30, 5, 'shop-glass');
      for (var x = 10; x < 34; x += 4) p.rect(x, 4, 1, 3, '~shop-glass');
      [12, 17, 24, 29].forEach(function (x, i) {
        p.px(x, 6, i % 2 ? 'room-4' : 'room-2');
      });
      p.px(8, 4, '^shop-glass');
      for (var k = 0; k < 4; k++) {
        var l = 1 + Math.floor(k / 2);
        var r = 39 - k * 2;
        p.rect(l, 7 + k, r - l + 1, 1, k === 0 ? 'ink' : k === 1 ? 'trim' : 'hull-2');
        p.px(l, 7 + k, 'ink');
        p.px(r, 7 + k, 'ink');
      }
      p.rect(2, 11, 31, 1, 'ink');
    } },
    { name: 'sejlbaad', speed: 8, w: 22, h: 25, draw: function (p) {
      dannebrog(p, 12, 0, 6, true);
      p.rect(11, 0, 1, 20, 'beam');
      for (var y = 5; y <= 18; y++) {
        var w = Math.round(((y - 4) / 14) * 8);
        p.rect(10 - w, y, w, 1, 'paper');
        p.px(10 - w, y, 'ink');
      }
      for (var y2 = 6; y2 <= 18; y2++) {
        var w2 = Math.round(((y2 - 5) / 13) * 6);
        p.rect(12, y2, w2, 1, 'paper');
        p.px(12 + w2, y2, 'ink');
      }
      p.rect(2, 19, 9, 1, 'beam');
      for (var k = 0; k < 5; k++) {
        var l = 2 + Math.floor(k / 2);
        var r = 20 - k;
        p.rect(l, 20 + k, r - l + 1, 1, k === 0 || k === 4 ? 'ink' : k === 1 ? 'trim' : 'hull-3');
        p.px(l, 20 + k, 'ink');
        p.px(r, 20 + k, 'ink');
      }
    } },
    { name: 'goboat', speed: 5, w: 16, h: 9, draw: function (p) {
      p.rect(2, 0, 12, 1, 'ink');
      p.rect(2, 1, 12, 1, 'dark-glass');
      p.rect(3, 2, 1, 4, 'ink');
      p.rect(12, 2, 1, 4, 'ink');
      p.px(6, 4, 'room-4');
      p.px(6, 5, 'door-2');
      p.px(9, 4, 'room-2');
      p.px(9, 5, 'parasol-1');
      p.rect(0, 6, 16, 1, 'ink');
      p.rect(1, 7, 14, 1, 'hull-1');
      p.px(0, 7, 'ink');
      p.px(15, 7, 'ink');
      p.rect(2, 8, 12, 1, 'ink');
    } },
    { name: 'kajak', speed: 7, w: 14, h: 6, draw: function (p) {
      p.px(6, 0, 'room-4');
      p.rect(6, 1, 1, 2, 'hull-2');
      p.rect(3, 2, 8, 1, 'beam');
      p.rect(2, 1, 1, 3, 'lampshade');
      p.rect(11, 1, 1, 3, 'lampshade');
      p.rect(1, 4, 12, 1, 'flag');
      p.px(13, 4, 'flag');
      p.rect(2, 5, 10, 1, '~flag');
    } },
  ];

  function sailerCanvas(C, kind) {
    var cv = document.createElement('canvas');
    cv.width = kind.w;
    cv.height = kind.h;
    kind.draw(new Pen(cv.getContext('2d'), C));
    return cv;
  }

  // Far away behind the roofs: Rundetårn and the spiral spire of Vor
  // Frelsers Kirke, in the haze colour.
  function skylineCanvas(C) {
    var cv = document.createElement('canvas');
    cv.width = 46;
    cv.height = 70;
    var p = new Pen(cv.getContext('2d'), C);
    p.rect(6, 37, 6, 3, 'far');
    p.rect(7, 36, 4, 1, 'far');
    p.rect(1, 40, 16, 1, 'far');
    for (var x = 1; x < 17; x += 2) p.px(x, 41, 'far');
    p.rect(2, 42, 14, 28, 'far');
    [[8, 47], [5, 54], [11, 61]].forEach(function (w) {
      p.rect(w[0], w[1], 2, 3, '~far');
    });
    p.rect(26, 36, 12, 34, 'far');
    p.rect(25, 35, 14, 1, 'far');
    p.rect(30, 43, 4, 6, '~far');
    p.rect(30, 54, 4, 4, '~far');
    for (var y = 8; y < 35; y++) {
      var half = Math.round(1 + ((y - 8) / 27) * 4);
      p.rect(32 - half, y, 2 * half, 1, 'far');
      p.px(32 - half + ((y * 2) % (2 * half)), y, '^far');
    }
    p.rect(31, 2, 1, 4, 'far');
    p.rect(31, 5, 2, 2, 'lampshade');
    return cv;
  }

  function cloudCanvas(C, seed) {
    var rand = rng(seed);
    var w = 14 + pick(rand, 8);
    var h = 6 + pick(rand, 2);
    var blobs = [
      [w * 0.3, h * 0.65, h * 0.38],
      [w * 0.55, h * 0.45, h * 0.5],
      [w * 0.75, h * 0.65, h * 0.34],
    ];
    var inside = function (x, y) {
      if (y > h - 2) return false;
      return blobs.some(function (b) {
        return (x + 0.5 - b[0]) * (x + 0.5 - b[0]) + (y + 0.5 - b[1]) * (y + 0.5 - b[1]) <= b[2] * b[2];
      }) || (y >= h * 0.6 && y <= h - 2 && x > w * 0.18 && x < w * 0.88);
    };
    var cv = document.createElement('canvas');
    cv.width = w;
    cv.height = h;
    var p = new Pen(cv.getContext('2d'), C);
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        if (!inside(x, y)) continue;
        var edge = !inside(x - 1, y) || !inside(x + 1, y) || !inside(x, y - 1) || !inside(x, y + 1);
        var under = !inside(x, y + 1) || !inside(x, y + 2);
        p.px(x, y, edge ? 'ink' : under ? '~cloud' : 'cloud');
      }
    }
    return cv;
  }

  function gullCanvas(C) {
    var cv = document.createElement('canvas');
    cv.width = 7;
    cv.height = 3;
    var p = new Pen(cv.getContext('2d'), C);
    ['0110110', '1001001'].forEach(function (row, y) {
      for (var x = 0; x < 7; x++) if (row.charAt(x) === '1') p.px(x, y, 'ink');
    });
    return cv;
  }

  // ---- The street component -------------------------------------------------------
  var ICON_CLOSE = '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false" shape-rendering="crispEdges"><path d="M3 3h2v2H3zM5 5h2v2H5zM7 7h2v2H7zM9 9h2v2H9zM11 11h2v2h-2zM11 3h2v2h-2zM9 5h2v2H9zM5 9h2v2H5zM3 11h2v2H3z" fill="currentColor"/></svg>';

  function Street(root, data) {
    this.root = root;
    this.data = data || {};
    this.opts = Object.assign(
      {
        windowsPerBuilding: 12, shopfronts: true, shopSlots: 2, contactUrl: '/pages/contact', shopUrl: '/collections/all',
        pageUrl: '/pages/gaden', minBuildings: 6, goal: 0, inviteBelow: 0, families: {}, deepLink: false,
      },
      this.data.settings || {}
    );
    var palette = (this.palette = this.data.palette || {});
    // Oldest first, so a new window always lands after the ones already
    // there and nobody's window moves house. Ties keep the data's order.
    this.windows = (this.data.windows || [])
      .map(function (w, i) {
        var n = normalizeWindow(w, palette);
        n.order = i;
        return n;
      })
      .sort(function (a, b) {
        return a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : a.order - b.order;
      });
    this.filter = { family: '' };
    // Enough houses to fill the width even while the street is young, so it
    // never ends in empty sky (the extra houses just have unlit windows).
    var scale = this.computeScale();
    var guess = Math.ceil((root.clientWidth || window.innerWidth) / (scale * 44)) + 1;
    this.opts.minBuildings = Math.max(parseInt(this.opts.minBuildings, 10) || 1, guess);
    this.shops = this.shopList();
    this.buildings = planStreet(this.windows, this.shops, this.opts);
    this.slotByWin = {};
    var self = this;
    this.buildings.forEach(function (bl) {
      bl.slots.forEach(function (slot) {
        if (slot.win) self.slotByWin[slot.index] = { bl: bl, slot: slot };
      });
    });
    this.C = readColors(root);
    this.cleanups = [];
    this.timers = [];
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.hoverable = window.matchMedia('(hover: hover) and (pointer: fine)');
    this.build();
  }

  Street.prototype.shopList = function () {
    if (!this.opts.shopfronts) return [];
    var real = (this.data.shopfronts || []).filter(function (s) {
      return s && s.active !== false && (s.brand || s.logo);
    });
    var slots = Math.max(real.length, parseInt(this.opts.shopSlots, 10) || 0);
    var out = [];
    for (var i = 0; i < slots; i++) out.push(real[i] || { placeholder: true, url: this.opts.contactUrl });
    return out;
  };

  Street.prototype.on = function (target, type, fn, opts) {
    target.addEventListener(type, fn, opts);
    this.cleanups.push(function () {
      target.removeEventListener(type, fn, opts);
    });
  };

  // A whole number of device pixels per art pixel keeps every pixel square.
  Street.prototype.computeScale = function () {
    var dpr = window.devicePixelRatio || 1;
    var cap = parseFloat(getComputedStyle(this.root).getPropertyValue('--street-max-height')) || 720;
    var narrow = window.innerWidth < 600;
    var target = Math.min(window.innerHeight * (narrow ? 0.62 : 0.8), cap);
    return Math.max(1, Math.floor((target / H) * dpr) / dpr);
  };

  Street.prototype.build = function () {
    var self = this;
    var root = this.root;

    var count = root.querySelector('[data-street-count]');
    if (count) count.textContent = this.windows.length + ' hjem på Gaden';
    this.renderInvite();
    this.renderGoal();

    var viewport = root.querySelector('[data-street-viewport]');
    if (!viewport) {
      viewport = document.createElement('div');
      viewport.setAttribute('data-street-viewport', '');
      root.appendChild(viewport);
    }
    viewport.classList.add('street__viewport');
    viewport.innerHTML =
      '<div class="street__sky" aria-hidden="true"></div>' +
      '<div class="street__scroller" tabindex="0" role="region" aria-label="Gaden. Rul vandret for at se flere huse."><div class="street__track"></div></div>';
    this.viewport = viewport;
    this.scroller = viewport.querySelector('.street__scroller');
    this.track = viewport.querySelector('.street__track');

    var sky = viewport.querySelector('.street__sky');
    [11, 23, 37].forEach(function (seed, i) {
      var cv = cloudCanvas(self.C, seed);
      cv.className = 'street-pixels street__cloud street__cloud--' + (i + 1);
      cv.dataset.w = cv.width;
      sky.appendChild(cv);
    });
    var gull = gullCanvas(this.C);
    gull.className = 'street-pixels street__gull';
    gull.dataset.w = 7;
    sky.appendChild(gull);
    var far = skylineCanvas(this.C);
    far.className = 'street-pixels street__far';
    sky.insertBefore(far, sky.firstChild);

    var html = ['<div class="street__canal" aria-hidden="true"></div>', '<div class="street-bldg street-bldg--plaza" style="aspect-ratio: ' + PLAZA_W + ' / ' + H + '"></div>'];
    this.buildings.forEach(function (bl) {
      html.push('<div class="street-bldg" data-b="' + bl.index + '" style="aspect-ratio: ' + bl.W + ' / ' + H + '"></div>');
    });
    html.push('<div class="street-bldg street-bldg--lot" style="aspect-ratio: ' + LOT_W + ' / ' + H + '"></div>');
    this.track.innerHTML = html.join('');
    var lot = this.track.querySelector('.street-bldg--lot');
    lot.appendChild(this.canvas(LOT_W, function (ctx) {
      drawLot(ctx, self.C);
    }));
    this.track.querySelector('.street-bldg--plaza').appendChild(this.canvas(PLAZA_W, function (ctx) {
      drawPlaza(ctx, self.C);
    }));
    this.canal = this.track.querySelector('.street__canal');
    this.canal.style.backgroundImage = 'url(' + waterTile(this.C).toDataURL() + ')';

    this.card = document.createElement('div');
    this.card.className = 'street-card';
    this.card.hidden = true;
    this.card.setAttribute('role', 'dialog');
    this.card.setAttribute('aria-label', 'Vindue');
    root.appendChild(this.card);

    this.renderFilters();
    this.applyScale();
    this.observe();
    this.wireScroll();
    this.wireWindows();
    this.wireCard();
    this.startLife();
    if (this.opts.deepLink) this.openFromUrl();
  };

  // ---- Invitation, goal and filters ---------------------------------------------
  // A young street says so and invites people in, rather than looking empty.
  Street.prototype.renderInvite = function () {
    var el = this.root.querySelector('[data-street-invite]');
    if (el) el.hidden = !(this.windows.length < (parseInt(this.opts.inviteBelow, 10) || 0));
  };

  Street.prototype.renderGoal = function () {
    var el = this.root.querySelector('[data-street-goal]');
    var goal = parseInt(this.opts.goal, 10) || 0;
    if (!el) return;
    el.hidden = !goal;
    if (!goal) return;
    var n = this.windows.length;
    var done = n >= goal;
    var text = done
      ? this.opts.goalReachedText || 'Gaden er fuld!'
      : n + ' af ' + goal + ' hjem' + (this.opts.goalText ? ' – ' + this.opts.goalText : '');
    var bar = '<span class="street-goal__bar" role="progressbar" aria-label="Hjem på Gaden" aria-valuemin="0" aria-valuemax="' + goal +
      '" aria-valuenow="' + Math.min(n, goal) + '"><span class="street-goal__fill" style="width:' + pct(Math.min(n, goal), goal) + '"></span></span>';
    var party = '';
    if (done) {
      for (var i = 0; i < 14; i++) party += '<span class="street-goal__bit" style="--i:' + i + '"></span>';
      party = '<span class="street-goal__party" aria-hidden="true">' + party + '</span>';
    }
    el.classList.toggle('is-reached', done);
    el.innerHTML = bar + party + '<p class="street-goal__text">' + esc(text) + '</p>';
  };

  Street.prototype.familyList = function (name) {
    return (this.opts.families[name] || []).map(function (s) {
      return String(s).trim().toLowerCase();
    });
  };

  Street.prototype.matches = function (win) {
    var f = this.filter;
    if (!win) return true;
    if (f.family && this.familyList(f.family).indexOf(win.front.name.toLowerCase()) === -1) return false;
    return true;
  };

  // Chips for the colour families that actually occur. A filter
  // dims the windows that don't match, so the street itself stays intact.
  Street.prototype.renderFilters = function () {
    var box = this.root.querySelector('[data-street-filters]');
    if (!box) return;
    var self = this;
    var families = Object.keys(this.opts.families || {}).filter(function (name) {
      var list = self.familyList(name);
      return self.windows.some(function (w) {
        return list.indexOf(w.front.name.toLowerCase()) > -1;
      });
    });
    box.hidden = this.windows.length < 2 || families.length < 2;
    if (box.hidden) {
      box.innerHTML = '';
      return;
    }
    function group(key, label, values) {
      if (values.length < 2) return '';
      return (
        '<div class="street-filter" role="group" aria-label="' + esc(label) + '"><span class="street-filter__label" aria-hidden="true">' + esc(label) + '</span>' +
        [''].concat(values).map(function (v) {
          return '<button type="button" class="street-filter__chip" data-filter="' + key + '" data-value="' + esc(v) + '" aria-pressed="' + (self.filter[key] === v) + '">' + esc(v || 'Alle') + '</button>';
        }).join('') +
        '</div>'
      );
    }
    box.innerHTML = group('family', 'Farver', families) + '<p class="street-filter__status" aria-live="polite" data-filter-status></p>';
    this.on(box, 'click', function (e) {
      var chip = e.target.closest('[data-filter]');
      if (chip) self.setFilter(chip.getAttribute('data-filter'), chip.getAttribute('data-value'));
    });
  };

  Street.prototype.setFilter = function (key, value) {
    var self = this;
    this.filter[key] = value;
    var box = this.root.querySelector('[data-street-filters]');
    [].forEach.call(box.querySelectorAll('[data-filter="' + key + '"]'), function (b) {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-value') === value));
    });
    [].forEach.call(this.track.querySelectorAll('.street-win'), function (btn) {
      btn.classList.toggle('is-dim', !self.matches(self.winFor(btn)));
    });
    var hits = [];
    this.windows.forEach(function (w, i) {
      if (self.matches(w)) hits.push(i);
    });
    var active = this.filter.family;
    var status = box.querySelector('[data-filter-status]');
    if (status) status.textContent = active ? hits.length + ' af ' + this.windows.length + ' vinduer passer' : '';
    this.closeCard();
    // Bring the first match into view when none is on screen.
    if (active && hits.length) {
      var sc = this.scroller;
      var seen = hits.some(function (i) {
        var x = self.windowX(i);
        return x > sc.scrollLeft && x < sc.scrollLeft + sc.clientWidth;
      });
      if (!seen) sc.scrollTo({ left: Math.max(0, this.windowX(hits[0]) - sc.clientWidth / 2), behavior: this.reduced.matches ? 'auto' : 'smooth' });
    }
  };

  // Where a window's centre sits in the scroller, in CSS pixels.
  Street.prototype.windowX = function (idx) {
    var where = this.slotByWin[idx];
    if (!where) return 0;
    var el = this.track.querySelector('.street-bldg[data-b="' + where.bl.index + '"]');
    return el.offsetLeft + ((where.slot.x + WIN_W / 2) / where.bl.W) * el.offsetWidth;
  };

  // ---- Personal links: /pages/gaden?vindue=<id> ------------------------------------
  // Scrolls to the window, gives it a short spotlight and opens its card.
  Street.prototype.openFromUrl = function () {
    var id = new URLSearchParams(location.search).get('vindue');
    if (!id) return;
    var idx = -1;
    this.windows.forEach(function (w, i) {
      if (w.id === id) idx = i;
    });
    var where = this.slotByWin[idx];
    if (!where) return;
    var self = this;
    var el = this.track.querySelector('.street-bldg[data-b="' + where.bl.index + '"]');
    this.scroller.scrollLeft = Math.max(0, this.windowX(idx) - this.scroller.clientWidth / 2);
    this.renderBuilding(el);
    var btn = el.querySelector('[data-win="' + idx + '"]');
    if (!btn) return;
    this.root.scrollIntoView({ block: 'center', behavior: this.reduced.matches ? 'auto' : 'smooth' });
    btn.classList.add('is-spotlight');
    this.timers.push(setTimeout(function () {
      if (btn.isConnected) self.openCard(btn, true);
    }, this.reduced.matches ? 0 : 700));
  };

  Street.prototype.permalink = function (win) {
    var url = new URL(this.opts.pageUrl || location.pathname, location.href);
    url.search = '';
    url.searchParams.set('vindue', win.id);
    return url.href;
  };

  Street.prototype.canvas = function (w, draw) {
    var cv = document.createElement('canvas');
    cv.width = w;
    cv.height = H;
    cv.className = 'street-pixels street-bldg__canvas';
    cv.setAttribute('aria-hidden', 'true');
    draw(cv.getContext('2d'));
    return cv;
  };

  Street.prototype.applyScale = function () {
    var s = (this.scale = this.computeScale());
    this.viewport.style.height = H * s + 'px';
    this.track.style.setProperty('--street-px', s + 'px');
    [].forEach.call(this.viewport.querySelectorAll('.street__sky canvas'), function (cv) {
      cv.style.width = cv.width * s + 'px';
      cv.style.height = cv.height * s + 'px';
    });
    var far = this.viewport.querySelector('.street__far');
    if (far) far.style.top = (SKY + 10) * s + 'px';
    if (this.canal) {
      this.canal.style.height = CANAL * s + 'px';
      this.canal.style.backgroundSize = TILE * s + 'px ' + CANAL * 2 * s + 'px';
      this.canal.style.setProperty('--street-canal-h', CANAL * s + 'px');
    }
    if (this.boat) this.placeBoat();
  };

  // Render buildings within one screen-width of the viewport; empty the rest.
  Street.prototype.observe = function () {
    var self = this;
    var els = this.track.querySelectorAll('.street-bldg[data-b]');
    if (!('IntersectionObserver' in window)) {
      [].forEach.call(els, function (el) {
        self.renderBuilding(el);
      });
      return;
    }
    this.io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) self.renderBuilding(e.target);
          else self.unrenderBuilding(e.target);
        });
      },
      { root: this.scroller, rootMargin: '0px 100% 0px 100%' }
    );
    [].forEach.call(els, function (el) {
      self.io.observe(el);
    });
  };

  Street.prototype.renderBuilding = function (el) {
    if (el.firstChild) return;
    var self = this;
    var bl = this.buildings[parseInt(el.getAttribute('data-b'), 10)];
    var cv = this.canvas(bl.W, function (ctx) {
      drawBuilding(ctx, self.C, bl);
    });
    el._ctx = cv.getContext('2d');
    var html = [];
    bl.slots.forEach(function (slot) {
      if (!slot.win) return;
      html.push(
        '<button type="button" class="street-win' + (self.matches(slot.win) ? '' : ' is-dim') + '" data-win="' + slot.index + '" aria-label="' + esc(slot.win.label) +
          '" style="left:' + pct(slot.x, bl.W) + ';top:' + pct(slot.y, H) + ';width:' + pct(WIN_W, bl.W) + ';height:' + pct(WIN_H, H) + '">' +
          '<img class="street-win__cover" alt="" decoding="async" hidden></button>'
      );
    });
    if (bl.shop) html.push(this.shopLink(bl));
    el.innerHTML = html.join('');
    el.insertBefore(cv, el.firstChild);
    [].forEach.call(el.querySelectorAll('.street-win'), function (btn) {
      var img = btn.querySelector('img');
      coverUrl(self.winFor(btn), self.opts).then(function (src) {
        img.src = src;
        img.hidden = false;
      }, function () {});
    });
  };

  Street.prototype.unrenderBuilding = function (el) {
    if (!el.firstChild) return;
    if (el.contains(document.activeElement)) return;
    if (this.openBtn && el.contains(this.openBtn)) return;
    el._ctx = null;
    el.innerHTML = '';
  };

  Street.prototype.shopLink = function (bl) {
    var s = shopGeom(bl);
    var shop = bl.shop;
    var url = shop.url || this.opts.contactUrl;
    var label = shop.placeholder ? 'Din butik her? Skriv til mig om en plads på Gaden' : shop.brand;
    var x = Math.min(s.sx, s.gx);
    var y = s.sy;
    var w = Math.max(s.sx + s.sw, s.gx + s.gw) - x;
    var h = s.gy + s.gh - y;
    var glass;
    if (shop.placeholder) glass = '<span class="street-shop__note">Skriv til mig</span>';
    else if (shop.logo) glass = '<img class="street-shop__logo" src="' + esc(shop.logo) + '" alt="" loading="lazy" decoding="async">';
    else glass = '';
    var external = /^https?:/i.test(url) && url.indexOf(location.host) === -1;
    return (
      '<a class="street-shop' + (shop.placeholder ? ' is-placeholder' : '') + '" href="' + esc(url) + '"' + (external ? ' target="_blank" rel="noopener"' : '') +
      ' aria-label="' + esc(label) + '" style="left:' + pct(x, bl.W) + ';top:' + pct(y, H) + ';width:' + pct(w, bl.W) + ';height:' + pct(h, H) + '">' +
      '<span class="street-shop__glass" style="left:' + pct(s.gx - x + 2, w) + ';width:' + pct(s.gw - 4, w) + ';top:' + pct(s.gy - y + 2, h) + ';bottom:2%">' + glass + '</span>' +
      '</a>'
    );
  };

  // ---- Scrolling ----------------------------------------------------------------
  Street.prototype.wireScroll = function () {
    var self = this;
    var sc = this.scroller;
    var prev = this.root.querySelector('[data-street-prev]');
    var next = this.root.querySelector('[data-street-next]');

    function sync() {
      var max = sc.scrollWidth - sc.clientWidth - 2;
      if (prev) prev.disabled = sc.scrollLeft <= 2;
      if (next) next.disabled = sc.scrollLeft >= max;
      if (self.openBtn) self.positionCard();
    }
    function go(dir) {
      sc.scrollBy({ left: dir * sc.clientWidth * 0.8, behavior: self.reduced.matches ? 'auto' : 'smooth' });
    }
    if (prev) this.on(prev, 'click', function () { go(-1); });
    if (next) this.on(next, 'click', function () { go(1); });
    this.on(sc, 'scroll', sync, { passive: true });
    this.on(window, 'resize', function () {
      self.applyScale();
      sync();
    });
    sync();

    // Drag to scroll with a mouse; touch already swipes natively.
    var drag = null;
    this.on(sc, 'pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      drag = { x: e.clientX, left: sc.scrollLeft, moved: false, id: e.pointerId };
    });
    this.on(sc, 'pointermove', function (e) {
      if (!drag) return;
      var dx = e.clientX - drag.x;
      if (!drag.moved && Math.abs(dx) > 6) {
        drag.moved = true;
        sc.setPointerCapture(drag.id);
        sc.classList.add('is-dragging');
        self.closeCard();
      }
      if (drag.moved) sc.scrollLeft = drag.left - dx;
    });
    var end = function () {
      if (!drag) return;
      if (drag.moved) {
        self.suppressClick = true;
        setTimeout(function () { self.suppressClick = false; }, 0);
      }
      sc.classList.remove('is-dragging');
      drag = null;
    };
    this.on(sc, 'pointerup', end);
    this.on(sc, 'pointercancel', end);
  };

  // ---- Windows and the card -----------------------------------------------------
  Street.prototype.winFor = function (btn) {
    return this.windows[parseInt(btn.getAttribute('data-win'), 10)];
  };

  Street.prototype.wireWindows = function () {
    var self = this;
    var track = this.track;

    this.on(track, 'click', function (e) {
      var btn = e.target.closest('.street-win');
      if (!btn) return;
      if (self.suppressClick) return;
      if (self.openBtn === btn && self.pinned) return self.closeCard(true);
      self.openCard(btn, true);
    }, true);

    this.on(track, 'pointerover', function (e) {
      if (!self.hoverable.matches || self.pinned) return;
      var btn = e.target.closest('.street-win');
      if (!btn) return;
      clearTimeout(self.hoverClose);
      clearTimeout(self.hoverOpen);
      self.hoverOpen = setTimeout(function () { self.openCard(btn, false); }, 140);
    });
    this.on(track, 'pointerout', function (e) {
      if (!self.hoverable.matches || self.pinned) return;
      if (!e.target.closest('.street-win')) return;
      clearTimeout(self.hoverOpen);
      self.hoverClose = setTimeout(function () { self.closeCard(); }, 220);
    });
    this.on(this.card, 'pointerenter', function () { clearTimeout(self.hoverClose); });
    this.on(this.card, 'pointerleave', function () {
      if (!self.pinned) self.hoverClose = setTimeout(function () { self.closeCard(); }, 220);
    });
  };

  Street.prototype.designUrl = function (win) {
    var base = win.url || this.opts.shopUrl;
    if (!win.url) return base;
    var q = ['ramme=' + encodeURIComponent(win.frame.name), 'forside=' + encodeURIComponent(win.front.name)];
    if (win.sw) q.push('kontakt=' + encodeURIComponent(win.sw.name));
    var felter = win.cells.filter(function (c) { return c === 1 || c === 2 || c === 'stik'; });
    if (felter.length) q.push('felter=' + encodeURIComponent(felter.join(',')));
    return base + (base.indexOf('?') > -1 ? '&' : '?') + q.join('&');
  };

  Street.prototype.openCard = function (btn, pinned) {
    var win = this.winFor(btn);
    if (!win) return;
    var where = this.slotByWin[btn.getAttribute('data-win')];
    var room = where ? 'room-' + where.slot.room : 'room-1';
    if (this.openBtn && this.openBtn !== btn) this.openBtn.removeAttribute('aria-expanded');

    var rows = [['Ramme', win.frame], ['Forside', win.front]];
    if (win.sw) rows.push(['Kontakt', win.sw]);
    var meta = win.month;
    var canShare = win.id && this.opts.share !== false;
    this.card.innerHTML =
      '<button type="button" class="street-card__close" aria-label="Luk">' + ICON_CLOSE + '</button>' +
      '<div class="street-card__art" style="background:' + esc(this.C[room]) + '"><img class="street-card__cover" alt="" hidden></div>' +
      '<div class="street-card__body">' +
      (meta ? '<p class="street-card__month">' + esc(meta) + '</p>' : '') +
      '<h3 class="street-card__title" tabindex="-1">' + esc(win.title) + '</h3>' +
      '<p class="street-card__model">' + esc(win.desc) + '</p>' +
      '<ul class="street-card__colors">' +
      rows.map(function (r) {
        return '<li><span class="street-card__swatch" style="background:' + esc(r[1].hex) + '"></span><span class="street-card__part">' + r[0] + '</span><span class="street-card__name">' + esc(r[1].name) + '</span></li>';
      }).join('') +
      '</ul>' +
      '<div class="street-card__actions">' +
      '<a class="street-card__cta" href="' + esc(this.designUrl(win)) + '">Design din egen</a>' +
      (canShare ? '<button type="button" class="street-card__share" data-share>Del dit vindue</button>' : '') +
      '</div>' +
      '<div class="street-card__shared" data-share-panel hidden></div>' +
      '</div>';
    var img = this.card.querySelector('.street-card__cover');
    coverUrl(win, this.opts).then(function (src) {
      img.src = src;
      img.hidden = false;
    }, function () {});
    this.card.setAttribute('aria-label', win.label);
    this.card.hidden = false;
    this.card.classList.toggle('is-pinned', !!pinned);
    this.openBtn = btn;
    this.pinned = !!pinned;
    btn.setAttribute('aria-expanded', 'true');
    this.positionCard();
    if (pinned) this.card.querySelector('.street-card__title').focus({ preventScroll: true });
  };

  Street.prototype.positionCard = function () {
    var btn = this.openBtn;
    var card = this.card;
    if (!btn || !btn.isConnected) return this.closeCard();
    if (window.matchMedia('(max-width: 600px)').matches) {
      card.classList.add('is-sheet');
      card.style.left = card.style.top = '';
      return;
    }
    card.classList.remove('is-sheet');
    var rr = this.root.getBoundingClientRect();
    var br = btn.getBoundingClientRect();
    var vr = this.scroller.getBoundingClientRect();
    if (br.right < vr.left || br.left > vr.right) return this.closeCard();
    var cw = card.offsetWidth;
    var ch = card.offsetHeight;
    var left = br.left + br.width / 2 - rr.left - cw / 2;
    left = Math.max(8, Math.min(rr.width - cw - 8, left));
    // Above the window if it fits inside the street, otherwise below it, so
    // the card never covers the heading and text above the street.
    var top = br.top - rr.top - ch - 10;
    if (top < vr.top - rr.top) top = br.bottom - rr.top + 10;
    card.style.left = Math.round(left) + 'px';
    card.style.top = Math.round(top) + 'px';
  };

  Street.prototype.closeCard = function (returnFocus) {
    if (this.card.hidden) return;
    var btn = this.openBtn;
    this.card.hidden = true;
    this.openBtn = null;
    this.pinned = false;
    if (btn) {
      btn.removeAttribute('aria-expanded');
      if (returnFocus && btn.isConnected) btn.focus({ preventScroll: true });
    }
  };

  Street.prototype.wireCard = function () {
    var self = this;
    this.on(this.card, 'click', function (e) {
      if (e.target.closest('.street-card__close')) return self.closeCard(true);
      if (e.target.closest('[data-share]') && self.openBtn) return self.showShare(self.openBtn);
      var copy = e.target.closest('[data-copy-link]');
      if (copy) {
        var done = function () {
          copy.textContent = 'Link kopieret';
        };
        if (navigator.clipboard) navigator.clipboard.writeText(copy.getAttribute('data-copy-link')).then(done, function () {});
        return;
      }
      var native = e.target.closest('[data-native-share]');
      if (native && self.shareFile) {
        navigator.share({ files: [self.shareFile], title: 'Mit hjem på Gaden', url: native.getAttribute('data-native-share') }).catch(function () {});
      }
    });
    this.on(document, 'keydown', function (e) {
      if (e.key === 'Escape' && !self.card.hidden) {
        e.preventDefault();
        self.closeCard(true);
      }
    });
    this.on(document, 'pointerdown', function (e) {
      if (self.card.hidden) return;
      if (self.card.contains(e.target) || e.target.closest('.street-win')) return;
      self.closeCard();
    });
  };

  // ---- "Del dit vindue": a picture made in the browser -----------------------------
  // The window in a crop of its street, the real cover in it, and a caption.
  // No server: per-window Open Graph images would need one, so the link
  // itself shares the page's one static image.
  var SHARE_SIZE = 1080;
  var SHARE_PX = 10; // art pixel size in the picture
  var SHARE_BAND = 200; // caption band at the bottom

  // Draws a cover image into a window's wall area (as .street-win__cover).
  function drawCoverImage(ctx, img, wx, wy, px) {
    var bw = WIN_W * px * 0.75;
    var bh = WIN_H * px * 0.6;
    var k = Math.min(bw / (img.naturalWidth || bw), bh / (img.naturalHeight || bh));
    var iw = (img.naturalWidth || bw) * k;
    var ih = (img.naturalHeight || bh) * k;
    ctx.drawImage(img, wx + (WIN_W * px - iw) / 2, wy + WIN_H * px * 0.25 + (bh - ih) / 2, iw, ih);
  }

  Street.prototype.shareImage = function (idx) {
    var self = this;
    var win = this.windows[idx];
    var where = this.slotByWin[idx];
    var C = this.C;
    var sky = getComputedStyle(this.root).getPropertyValue('--street-sky').trim() || '#a8d8ea';

    // The window's house and its neighbours, side by side at 1 px per art pixel.
    var b = where.bl.index;
    var parts = [b - 2, b - 1, b, b + 1, b + 2].map(function (k) {
      if (k === -1) return { W: PLAZA_W, plaza: true };
      if (k < 0) return null;
      if (k > self.buildings.length) return null;
      if (k === self.buildings.length) return { W: LOT_W, lot: true };
      return self.buildings[k];
    }).filter(Boolean);
    var stripW = parts.reduce(function (s, p) { return s + p.W; }, 0);
    var strip = document.createElement('canvas');
    strip.width = stripW;
    strip.height = H;
    var sctx = strip.getContext('2d');
    sctx.fillStyle = sctx.createPattern(waterTile(C), 'repeat-x');
    sctx.translate(0, GROUND_Y + QUAY);
    sctx.fillRect(0, 0, stripW, CANAL);
    sctx.setTransform(1, 0, 0, 1, 0, 0);
    var x0 = 0;
    var winX = 0;
    var others = []; // every other window in view gets its own cover too
    parts.forEach(function (p) {
      var cv = document.createElement('canvas');
      cv.width = p.W;
      cv.height = H;
      if (p.plaza) drawPlaza(cv.getContext('2d'), C);
      else if (p.lot) drawLot(cv.getContext('2d'), C);
      else drawBuilding(cv.getContext('2d'), C, p);
      sctx.drawImage(cv, x0, 0);
      if (p === where.bl) winX = x0 + where.slot.x;
      (p.slots || []).forEach(function (slot) {
        if (slot.win && slot !== where.slot) others.push({ win: slot.win, x: x0 + slot.x, y: slot.y });
      });
      x0 += p.W;
    });

    var cropW = SHARE_SIZE / SHARE_PX;
    var cropH = (SHARE_SIZE - SHARE_BAND) / SHARE_PX;
    var cx = Math.round(Math.max(0, Math.min(stripW - cropW, winX + WIN_W / 2 - cropW / 2)));
    var cy = Math.round(Math.max(0, Math.min(H - cropH, where.slot.y + WIN_H / 2 - cropH / 2)));

    var out = document.createElement('canvas');
    out.width = SHARE_SIZE;
    out.height = SHARE_SIZE;
    var ctx = out.getContext('2d');
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, SHARE_SIZE, SHARE_SIZE);
    // A short street (fewer houses than the crop is wide) is centred on sky;
    // the source rectangle must stay inside the strip, or the browser
    // rescales it and the covers no longer line up with their windows.
    var shift = Math.max(0, Math.round((cropW - stripW) / 2));
    var srcW = Math.min(cropW, stripW);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(strip, cx, cy, srcW, cropH, shift * SHARE_PX, 0, srcW * SHARE_PX, cropH * SHARE_PX);

    var toX = function (x) { return (x - cx + shift) * SHARE_PX; };
    var toY = function (y) { return (y - cy) * SHARE_PX; };
    var wx = toX(winX);
    var wy = toY(where.slot.y);
    var inView = others.filter(function (o) {
      return toX(o.x) > -WIN_W * SHARE_PX && toX(o.x) < SHARE_SIZE && toY(o.y) > -WIN_H * SHARE_PX && toY(o.y) < SHARE_SIZE - SHARE_BAND;
    });
    var opts = this.opts;
    var images = function (list) {
      return Promise.all(list.map(function (o) {
        return coverUrl(o.win, opts).then(loadImage).catch(function () { return null; });
      }));
    };
    var fonts = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
    return Promise.all([coverUrl(win, opts).then(loadImage), images(inView), fonts]).then(function (res) {
      ctx.imageSmoothingEnabled = true;
      inView.forEach(function (o, i) {
        if (res[1][i]) drawCoverImage(ctx, res[1][i], toX(o.x), toY(o.y), SHARE_PX);
      });
      drawCoverImage(ctx, res[0], wx, wy, SHARE_PX);
      // A chunky frame round their window, so it's clear which one it is.
      var f = SHARE_PX;
      var ww = WIN_W * SHARE_PX;
      var wh = WIN_H * SHARE_PX;
      ctx.fillStyle = C.ink;
      ctx.fillRect(wx - 4 * f, wy - 4 * f, ww + 8 * f, wh + 8 * f);
      ctx.fillStyle = C.paper;
      ctx.fillRect(wx - 3 * f, wy - 3 * f, ww + 6 * f, wh + 6 * f);
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(strip, winX - 1, where.slot.y - 1, WIN_W + 2, WIN_H + 2, wx - f, wy - f, ww + 2 * f, wh + 2 * f);
      ctx.imageSmoothingEnabled = true;
      drawCoverImage(ctx, res[0], wx, wy, SHARE_PX);

      // Caption band.
      var top = SHARE_SIZE - SHARE_BAND;
      ctx.fillStyle = C.paper;
      ctx.fillRect(0, top, SHARE_SIZE, SHARE_BAND);
      ctx.fillStyle = C.ink;
      ctx.fillRect(0, top, SHARE_SIZE, 8);
      var family = getComputedStyle(self.root).fontFamily || 'sans-serif';
      ctx.textBaseline = 'alphabetic';
      ctx.font = '700 64px ' + family;
      ctx.fillText('Mit hjem på Gaden', 56, top + 98);
      ctx.font = '500 34px ' + family;
      ctx.globalAlpha = 0.75;
      ctx.fillText(win.title + '  ·  studieh.dk', 56, top + 152);
      ctx.globalAlpha = 1;
      // Outer pixel border.
      ctx.fillRect(0, 0, SHARE_SIZE, 8);
      ctx.fillRect(0, 0, 8, SHARE_SIZE);
      ctx.fillRect(SHARE_SIZE - 8, 0, 8, SHARE_SIZE);
      ctx.fillRect(0, SHARE_SIZE - 8, SHARE_SIZE, 8);
      return new Promise(function (resolve) {
        out.toBlob(resolve, 'image/png');
      });
    });
  };

  Street.prototype.showShare = function (btn) {
    var self = this;
    var idx = parseInt(btn.getAttribute('data-win'), 10);
    var win = this.windows[idx];
    var panel = this.card.querySelector('[data-share-panel]');
    var trigger = this.card.querySelector('[data-share]');
    if (!panel || !win) return;
    var link = this.permalink(win);
    panel.hidden = false;
    panel.innerHTML = '<p class="street-card__note">Laver dit billede…</p>';
    if (trigger) trigger.hidden = true;
    this.positionCard();
    this.shareImage(idx).then(function (blob) {
      if (!blob || self.openBtn !== btn) return;
      if (self.shareUrl) URL.revokeObjectURL(self.shareUrl);
      self.shareUrl = URL.createObjectURL(blob);
      var file = typeof File === 'function' ? new File([blob], 'mit-hjem-paa-gaden.png', { type: 'image/png' }) : null;
      self.shareFile = file;
      var native = file && navigator.canShare && navigator.canShare({ files: [file] });
      panel.innerHTML =
        '<img class="street-card__shot" src="' + self.shareUrl + '" alt="Dit vindue på Gaden med teksten Mit hjem på Gaden">' +
        '<div class="street-card__share-actions">' +
        (native ? '<button type="button" class="street-card__cta" data-native-share="' + esc(link) + '">Del billedet</button>' : '') +
        '<a class="street-card__cta' + (native ? ' is-quiet' : '') + '" href="' + self.shareUrl + '" download="mit-hjem-paa-gaden.png">Hent billede</a>' +
        '<button type="button" class="street-card__link" data-copy-link="' + esc(link) + '">Kopiér link</button>' +
        '</div>';
      self.positionCard();
    }, function () {
      panel.innerHTML = '<p class="street-card__note">Billedet kunne ikke laves. Del linket i stedet: <a href="' + esc(link) + '">' + esc(link) + '</a></p>';
    });
  };

  // ---- A little life: a lamp switched on now and then ----------------------------
  Street.prototype.startLife = function () {
    var self = this;
    var tick = function () {
      if (self.reduced.matches || document.hidden) return;
      var vr = self.scroller.getBoundingClientRect();
      var seen = [].filter.call(self.track.querySelectorAll('.street-win'), function (b) {
        var r = b.getBoundingClientRect();
        return r.right > vr.left && r.left < vr.right;
      });
      if (!seen.length) return;
      var btn = seen[Math.floor(Math.random() * seen.length)];
      var where = self.slotByWin[btn.getAttribute('data-win')];
      var el = btn.parentNode;
      if (!where || !el._ctx || where.slot.lit) return;
      var ctx = el._ctx;
      var p = new Pen(ctx, self.C);
      where.slot.lit = true;
      drawWindow(p, where.bl, where.slot, true);
      self.timers.push(setTimeout(function () {
        where.slot.lit = false;
        if (el._ctx === ctx) drawWindow(p, where.bl, where.slot, false);
      }, 2600 + Math.random() * 2400));
    };
    this.interval = setInterval(tick, 4200);
    this.timers.push(setTimeout(function () { self.sail(); }, 2500));
  };

  // ---- Boats passing on the canal --------------------------------------------------
  // One at a time, entering just outside the part of the street in view and
  // moving a whole art pixel per step, so it stays crisp.
  Street.prototype.sail = function () {
    var self = this;
    if (this.dead) return;
    if (this.reduced.matches || document.hidden) {
      this.timers.push(setTimeout(function () { self.sail(); }, 8000));
      return;
    }
    var n = this.boatCount = (this.boatCount || 0) + 1;
    var kind = SAILERS[(n - 1) % SAILERS.length];
    var dir = n % 2 ? 1 : -1;
    var cv = sailerCanvas(this.C, kind);
    cv.className = 'street-pixels street__boat';
    cv.setAttribute('aria-hidden', 'true');
    var s = this.scale;
    var sc = this.scroller;
    var from = Math.floor(sc.scrollLeft / s) - kind.w - 2;
    var to = Math.ceil((sc.scrollLeft + sc.clientWidth) / s) + 2;
    this.boat = { cv: cv, kind: kind, dir: dir, x: dir > 0 ? from : to, end: dir > 0 ? to : from };
    this.track.appendChild(cv);
    this.placeBoat();
    var last = performance.now();
    var acc = 0;
    var step = function (now) {
      var b = self.boat;
      if (!b || b.cv !== cv) return;
      acc += ((now - last) / 1000) * kind.speed;
      last = now;
      if (acc >= 1) {
        var k = Math.floor(acc);
        acc -= k;
        b.x += dir * k;
        self.placeBoat();
      }
      if ((dir > 0 && b.x >= b.end) || (dir < 0 && b.x <= b.end) || self.reduced.matches) {
        cv.remove();
        self.boat = null;
        self.timers.push(setTimeout(function () { self.sail(); }, 4000 + Math.random() * 10000));
        return;
      }
      self.raf = requestAnimationFrame(step);
    };
    this.raf = requestAnimationFrame(step);
  };

  Street.prototype.placeBoat = function () {
    var b = this.boat;
    var s = this.scale;
    b.cv.style.width = b.kind.w * s + 'px';
    b.cv.style.height = b.kind.h * s + 'px';
    b.cv.style.top = (H - 1 - b.kind.h) * s + 'px';
    b.cv.style.transform = 'translateX(' + b.x * s + 'px)' + (b.dir < 0 ? ' scaleX(-1)' : '');
  };

  Street.prototype.destroy = function () {
    this.dead = true;
    cancelAnimationFrame(this.raf);
    this.cleanups.forEach(function (fn) { fn(); });
    this.timers.forEach(clearTimeout);
    clearInterval(this.interval);
    clearTimeout(this.hoverOpen);
    clearTimeout(this.hoverClose);
    if (this.io) this.io.disconnect();
    if (this.card && this.card.parentNode) this.card.parentNode.removeChild(this.card);
  };

  // ---- Public API and auto-init -----------------------------------------------------
  function readData(root) {
    var el = root.querySelector('script[data-street-data]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (err) {
      console.error('Gaden: could not read the street data', err);
      return null;
    }
  }

  function mount(root, data) {
    if (root._street) root._street.destroy();
    data = data || readData(root);
    if (!data) return null;
    root._street = new Street(root, data);
    return root._street;
  }

  function initAll(scope) {
    [].forEach.call((scope || document).querySelectorAll('[data-street]:not([data-street-manual])'), function (root) {
      mount(root);
    });
  }

  window.StudieStreet = { mount: mount, normalizeWindow: normalizeWindow };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();

  // Theme editor: re-mount when the section is edited, tidy up when removed.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    [].forEach.call(e.target.querySelectorAll('[data-street]'), function (root) {
      if (root._street) root._street.destroy();
    });
  });
})();
